import { createSIPTool, createCompoundInterestTool, createPercentageTool } from "../factories";
import type { ToolConfig } from "../types";

export const investmentTools: ToolConfig[] = [
  // 1. SIP Calculator
  createSIPTool({
    slug: "sip-calculator",
    category: "investment",
    title: "SIP Calculator",
    shortTitle: "SIP Returns",
    description: "Calculate SIP returns with rupee-cost averaging for wealth building",
    keywords: ["sip", "mutual fund", "systematic investment", "monthly investment"],
    defaultMonthly: 10000,
    maxMonthly: 1000000,
    defaultRate: 12,
    defaultYears: 10,
    whatIs:
      "A Systematic Investment Plan (SIP) is a disciplined method to invest a fixed amount regularly—typically monthly—in mutual funds. Instead of trying to time the market with a lumpsum, SIP spreads investments across market cycles, reducing your exposure to timing risk through a process called rupee-cost averaging. When market prices are low, your fixed amount buys more units; when prices are high, it buys fewer units. This averaging effect smooths out market volatility and is particularly powerful in India's dynamic market conditions. SIP is ideal for salaried professionals, freelancers, and business owners looking to build long-term wealth for goals like retirement, child education, or home ownership. AMFI data shows that SIP investors achieve better psychological discipline and stick to investing even during market downturns, leading to superior long-term wealth creation. Most mutual funds allow SIPs starting as low as ₹500 per month, making it accessible to all income levels.",
    sections: [
      {
            "title": "Who Should Use This Calculator?",
            "body": "Anyone planning to start or analyze an SIP should use this calculator. Young professionals building wealth, salaried employees seeking regular investment discipline, pre-retirees accumulating corpus, and anyone without lumpsum capital available. If you earn regular income and want to build wealth systematically without market timing anxiety, SIP is your tool."
      },
      {
            "title": "How Does This Calculator Work?",
            "body": "The calculator uses the future value of annuity formula to compute your SIP maturity amount. It factors in: your monthly investment amount, expected annual returns, investment tenure, and compounding frequency (monthly). The formula accounts for rupee-cost averaging by treating each monthly investment as a separate investment growing for the remaining tenure."
      },
      {
            "title": "Key Regulations & Tax Rules",
            "body": "SIPs are regulated by SEBI through AMFI guidelines. Equity mutual fund SIPs with holding >1 year attract 10% Long-Term Capital Gains (LTCG) tax—no tax if gains are ≤₹1 lakh in a financial year. Debt SIPs held >3 years get indexation benefit, reducing taxable gains. Tax is only on gains, not on your invested principal. Always track your cost basis for tax purposes."
      },
      {
            "title": "Benefits of Using This Calculator",
            "body": "Quantifies your long-term wealth potential, motivates regular investing, helps set realistic financial goals, enables goal-based planning (retirement corpus, education fund), allows comparison across different return scenarios, and removes guesswork from investment projections. Seeing the power of compounding encourages discipline and early start."
      },
      {
            "title": "Practical Tips for SIP Success",
            "body": "Start early—20s give 30+ years of compounding. Increase SIP with salary hikes (step-up SIP). Invest in diversified funds rather than single sector funds. Avoid stopping during market downturns—downturns create cheaper units. Review annually but don't react to short-term volatility. Use auto-debit from salary accounts for guaranteed consistency."
      }
],
    faqs: [
      {
        question: "Can I increase my SIP amount over time?",
        answer:
          "Yes, most funds allow step-up SIPs where you can increase monthly investment annually. This helps keep pace with inflation and accelerates wealth creation as your income grows.",
      },
      {
        question: "What is the minimum SIP amount?",
        answer:
          "Most mutual funds require a minimum SIP of ₹500 to ₹1,000 per month. Some funds allow even lower amounts through auto-investing platforms.",
      },
      {
        question: "Is SIP safe?",
        answer:
          "SIP is as safe as the underlying mutual fund. Equity SIPs have market risk but benefit from rupee-cost averaging. Debt SIPs are lower-risk. Diversify across asset classes based on your risk profile.",
      },
      {
        question: "Can I stop my SIP anytime?",
        answer:
          "Yes, you can pause or stop your SIP anytime without penalty. However, continuing for the full tenure maximizes compounding returns.",
      },
      {
        question: "What returns should I expect from SIP?",
        answer:
          "Equity SIPs historically deliver 10-15% p.a. over 10+ years. Debt SIPs return 5-8% p.a. Past performance is not guaranteed; actual returns depend on fund performance and market conditions.",
      },
    ],
    relatedSlugs: [
      "investment/lumpsum-calculator",
      "investment/sip-vs-lumpsum",
      "investment/mutual-fund-returns",
      "investment/step-up-sip",
    ],
  }),

  // 2. Lumpsum Investment Calculator
  createCompoundInterestTool({
    slug: "lumpsum-calculator",
    category: "investment",
    title: "Lumpsum Investment Calculator",
    shortTitle: "Lumpsum Returns",
    description: "Calculate lumpsum investment returns and plan one-time capital deployment",
    keywords: ["lumpsum", "one-time investment", "mutual fund", "investment returns"],
    defaultAmount: 500000,
    maxAmount: 10000000,
    defaultRate: 12,
    defaultYears: 10,
    maxYears: 50,
    compoundingFrequency: 12,
    whatIs:
      "Lumpsum investing means deploying a large sum of money all at once into mutual funds, stocks, or securities rather than spreading it over time. While lumpsum is more exposed to market timing risk—you might invest just before a market crash—it offers a significant advantage: your entire capital compounds for the full tenure, potentially delivering higher absolute returns than SIP if timed well. Lumpsum is ideal for investors with substantial capital from sources like inheritance, property sales, business exits, or large bonuses. In India's long-term bull market (Nifty 50 historically delivers 10%+ CAGR), lumpsum investors who invested during any major dip recovered quickly and benefited from extended compounding. The key is a 5-10 year investment horizon and the psychological strength to stay invested through market corrections. High net worth individuals, business owners, and legacy investors often use lumpsum to deploy capital efficiently.",
    sections: [
      {
            "title": "Who Should Use This Calculator?",
            "body": "Investors with large capital from bonuses, inheritance, or asset sales. Those with high risk tolerance and 5-10+ year horizons. Business owners with retained earnings. High-income professionals with annual surpluses. Investors experienced in market cycles. Also useful for comparing lumpsum vs. SIP strategies to decide your optimal approach."
      },
      {
            "title": "How Does This Calculator Work?",
            "body": "Uses the compound interest formula: FV = PV × (1 + r)^n, where PV is your lumpsum, r is the monthly return rate, and n is the number of months. The calculator assumes the entire amount is invested immediately and compounds monthly at your expected annual return rate over the specified tenure."
      },
      {
            "title": "Key Regulations & Tax Implications",
            "body": "Regulated by SEBI. Equity mutual fund lumpsums with holding >1 year qualify for 10% LTCG tax, with no tax on first ₹1 lakh of gains. Debt funds held >3 years use indexation benefit. Dividend option attracts dividend tax before 2020; growth option defers tax till exit. Consult a CA for optimal fund choice based on your tax bracket."
      },
      {
            "title": "Benefits of Lumpsum Investing",
            "body": "Entire capital compounds immediately, maximizing returns in bull markets. Time in market beats timing. Reduces anxiety of monthly investment decisions. Efficient for deploying large capital. Historical data shows lumpsum often outperforms SIP over 10+ years in uptrending markets like India. Enables full capital deployment without psychological barriers."
      },
      {
            "title": "Risk Management & Best Practices",
            "body": "Don't try to time the bottom—invest when you have surplus capital. Diversify across large-cap, mid-cap, small-cap funds. Commit to 5-10 year horizon minimum. Don't panic sell during corrections—hold for recovery. Consider a hybrid: invest 70% lumpsum, 30% as SIP to balance compounding and timing risk. Review quarterly but rebalance only annually."
      }
],
      faqs: [
      {
        question: "Is lumpsum better than SIP?",
        answer:
          "Neither is universally better. Lumpsum works well in market downturns and with large capital. SIP is better for regular income investors and reduces timing risk. Consider a hybrid approach: invest 70% lumpsum, 30% as SIP.",
      },
      {
        question: "What is the best time to invest a lumpsum?",
        answer:
          "Time in the market beats timing the market. Market timing is nearly impossible. If you have surplus capital, invest it promptly rather than waiting for a crash that may not come.",
      },
      {
        question: "Can I invest a lumpsum and later add SIP?",
        answer:
          "Absolutely. Many investors use a hybrid strategy: invest a large lumpsum and then add monthly SIP. This combines the benefits of both approaches.",
      },
      {
        question: "Are there tax implications for lumpsum investing?",
        answer:
          "Equity mutual funds held >1 year are taxed at 10% (LTCG). Debt funds have indexation benefits. Consult a tax advisor based on your holding period and fund type.",
      },
      {
        question: "What happens if markets fall after I invest a lumpsum?",
        answer:
          "This is timing risk. If you stay invested for 5-10+ years, markets typically recover. Panic selling after a downturn is the biggest mistake. Stick to your investment plan.",
      },
    ],
    relatedSlugs: [
      "investment/sip-calculator",
      "investment/sip-vs-lumpsum",
      "investment/mutual-fund-returns",
    ],
  }),

  // 3. SWP Calculator
  {
    slug: "swp-calculator",
    category: "investment",
    title: "Systematic Withdrawal Plan Calculator",
    shortTitle: "SWP Planning",
    description:
      "Calculate monthly withdrawal amount and corpus depletion for retirement or income",
    keywords: [
      "systematic withdrawal",
      "swp",
      "withdrawal plan",
      "retirement income",
    ],
    inputs: [
      {
        type: "number",
        key: "corpus",
        label: "Invested Corpus",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "withdrawalRate",
        label: "Monthly Withdrawal Amount",
        prefix: "₹",
        min: 1000,
        max: 500000,
        step: 1000,
        default: 50000,
      },
      {
        type: "number",
        key: "rate",
        label: "Expected Return Rate",
        suffix: "% p.a.",
        min: 1,
        max: 20,
        step: 0.5,
        default: 8,
      },
    ],
    calculate(v) {
      const corpus = v.corpus as number;
      const monthly = v.withdrawalRate as number;
      const annual = v.rate as number;
      const monthlyRate = annual / 100 / 12;
      let balance = corpus;
      let months = 0;
      while (balance > 0 && months < 600) {
        balance = balance * (1 + monthlyRate) - monthly;
        months++;
      }
      const annualWithdrawal = monthly * 12;
      const withdrawalYield = (annualWithdrawal / corpus) * 100;
      return {
        corpus: corpus,
        monthlyWithdrawal: monthly,
        durationYears: Math.round(months / 12 * 10) / 10,
        durationMonths: months,
        totalWithdrawn: Math.round(monthly * months),
        annualWithdrawal: Math.round(annualWithdrawal),
        withdrawalYield: Math.round(withdrawalYield * 100) / 100,
      };
    },
    resultFields: [
      { key: "durationYears", label: "Corpus Will Last", format: "years", isPrimary: true },
      { key: "monthlyWithdrawal", label: "Monthly Withdrawal", format: "currency" },
      { key: "annualWithdrawal", label: "Annual Withdrawal", format: "currency" },
      { key: "withdrawalYield", label: "Withdrawal Yield", format: "percent" },
    ],
    content: {
      sections: [
      {
            "title": "Who Should Use This Calculator?",
            "body": "Retirees creating systematic retirement income. Early retirees during sabbaticals. Business owners paying dividends to themselves. NRIs supplementing overseas income. Anyone with accumulated corpus needing regular cash flow. Pre-retirees testing withdrawal sustainability. Investors planning bridge periods between retirement milestones."
      },
      {
            "title": "How Does SWP Math Work?",
            "body": "Each month: remaining balance grows by (1 + monthly return rate), then withdrawal is deducted. Calculator iterates this until corpus depletes or reaches 50 years (600 months). Output shows how many years/months corpus lasts at your withdrawal rate and expected returns, helping you size sustainable withdrawals correctly."
      },
      {
            "title": "SEBI Rules & Tax Optimization",
            "body": "SEBI allows SWP from all mutual funds. Tax only applies to gains—principal withdrawals are tax-free. Equity SWP (>1 year holding): 10% LTCG on gains. Debt SWP gains: taxed as income (slab rate), with indexation benefit if held >3 years. Strategy: In tax year 1, withdrawal is mostly principal (low tax). In later years, more gains are withdrawn (higher tax). Track your cost basis carefully."
      },
      {
            "title": "Sustainable Withdrawal Best Practices",
            "body": "Use 3-4% p.a. withdrawal rate (safe withdrawal)—this has 95%+ probability of corpus lasting 30+ years. Balance funds (60% equity, 40% debt) suit SWP better than pure equity due to lower volatility. Increase withdrawal by 3% annually to match inflation. Monitor corpus quarterly; if markets crash 20%, reduce withdrawal temporarily. Stop SWP if corpus falls below ₹5 lakh to preserve remaining corpus."
      },
      {
            "title": "Real-World Retirement Income Planning",
            "body": "Calculate corpus needed for desired monthly income: divide monthly need by safe withdrawal rate (0.25% monthly). For ₹50K monthly need: require ₹2 crore corpus (₹50K ÷ 0.0025). Combine SWP with pension (NPS, SSY), CPF, fixed income for diversified retirement. Use inflation calculator to adjust income needs annually. Annual review crucial—adjust for market returns and life changes."
      }
],
      
      whatIs:
        "A Systematic Withdrawal Plan (SWP) is the inverse of SIP—instead of investing monthly, you withdraw a fixed amount monthly from your invested corpus. The genius of SWP is that your remaining balance stays invested and continues growing, theoretically sustaining withdrawals indefinitely if your returns exceed withdrawals. In India, SWP is crucial for retirees transitioning from earning to spending phase, or professionals taking sabbaticals. Unlike lumpsum withdrawals where you deplete capital quickly, SWP preserves and grows remaining corpus through ongoing market returns. A ₹50 lakh corpus invested in balanced funds (8% returns) can sustainably withdraw ₹12,500-16,700 monthly (3-4% p.a.) indefinitely, protecting against inflation and market downturns. SWP also offers tax efficiency: only gains are taxed (10% LTCG for equity after 1 year), not principal withdrawals. Popular with NRIs, retirees, and bridge-period planners.",
      faqs: [
        {
          question: "What monthly withdrawal is sustainable from my corpus?",
          answer:
            "A safe withdrawal is 3-4% p.a. of your corpus (0.25-0.33% monthly). For ₹50 lakh, withdraw ₹12,500-16,700 monthly. This balances income needs with corpus preservation.",
        },
        {
          question: "Should I use SWP or lumpsum withdrawals?",
          answer:
            "SWP is better: remaining balance stays invested and continues growing, reducing sequence-of-returns risk. Lumpsum withdrawal leaves less to grow.",
        },
        {
          question: "What happens when the corpus depletes?",
          answer:
            "Plan your withdrawal amount to avoid full depletion. Use this calculator to find the sustainable withdrawal amount, or adjust investments to higher return funds.",
        },
        {
          question: "Can I increase SWP amount if returns are higher?",
          answer:
            "Yes, but cautiously. If markets deliver better returns, you can increase withdrawal by inflation (3-4% p.a.). Don't increase too aggressively, as bad years will quickly deplete corpus.",
        },
        {
          question: "Is SWP taxed?",
          answer:
            "Only the gains portion is taxed. Equity SWP after 1 year: 10% LTCG tax. Debt SWP gains are taxed as income. SWP itself is a withdrawal, not a taxable event.",
        },
      ],
      relatedSlugs: [
        "investment/retirement-income-calculator",
        "retirement/annuity-calculator",
        "retirement/retirement-corpus-calculator",
      ],
    },
  },

  // 4. Step-Up SIP
  {
    slug: "step-up-sip",
    category: "investment",
    title: "Step-Up SIP Calculator",
    shortTitle: "SIP with Annual Increase",
    description:
      "Calculate SIP returns with annual increase to match salary growth",
    keywords: [
      "step-up sip",
      "increasing sip",
      "accelerating investment",
      "salary hike",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyStart",
        label: "Starting Monthly Investment",
        prefix: "₹",
        min: 500,
        max: 500000,
        step: 500,
        default: 10000,
      },
      {
        type: "number",
        key: "annualIncrease",
        label: "Annual Increase",
        suffix: "%",
        min: 1,
        max: 30,
        step: 1,
        default: 10,
      },
      {
        type: "number",
        key: "rate",
        label: "Expected Return Rate",
        suffix: "% p.a.",
        min: 1,
        max: 30,
        step: 0.5,
        default: 12,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 1,
        max: 40,
        step: 1,
        default: 10,
      },
    ],
    calculate(v) {
      const startMonthly = v.monthlyStart as number;
      const annualIncrease = (v.annualIncrease as number) / 100;
      const annual = v.rate as number;
      const years = v.years as number;
      const monthlyRate = annual / 100 / 12;

      let totalValue = 0;
      let totalInvested = 0;

      for (let year = 0; year < years; year++) {
        const yearlyMonthly = startMonthly * Math.pow(1 + annualIncrease, year);
        for (let month = 0; month < 12; month++) {
          const monthsLeft = (years - year) * 12 - month;
          totalValue +=
            yearlyMonthly * Math.pow(1 + monthlyRate, monthsLeft - 1);
          totalInvested += yearlyMonthly;
        }
      }

      return {
        totalValue: Math.round(totalValue),
        invested: Math.round(totalInvested),
        returns: Math.round(totalValue - totalInvested),
        finalMonthly: Math.round(
          startMonthly * Math.pow(1 + annualIncrease, years - 1)
        ),
      };
    },
    resultFields: [
      { key: "totalValue", label: "Total Value", format: "currency", isPrimary: true },
      { key: "invested", label: "Total Invested", format: "currency" },
      { key: "returns", label: "Total Returns", format: "currency" },
      { key: "finalMonthly", label: "Final Monthly SIP", format: "currency" },
    ],
    chartSegments: [
      { label: "Invested", valueKey: "invested", color: "#3ecf8e" },
      { label: "Returns", valueKey: "returns", color: "#8b5cf6" },
    ],
    content: {
      sections: [
      {
            "title": "Ideal Candidates for Step-Up SIP",
            "body": "Young professionals (age 20-35) with rising career trajectories. Salaried employees receiving annual bonuses or hikes. Business owners with growing revenues. Freelancers with increasing income. Anyone whose income grows 5%+ annually. Professionals planning to work 25-30+ more years. Those starting small but expecting income growth."
      },
      {
            "title": "How Step-Up SIP Compounds Wealth",
            "body": "Calculator loops through years, computing monthly investment that grows by the step-up percentage annually. Each month's investment compounds for remaining tenure. Formula accounts for geometric progression of contributions plus monthly compounding. Result: significantly higher corpus than flat SIP due to time-weighted increases and longer compounding period for early investments."
      },
      {
            "title": "Regulatory Framework & Taxation",
            "body": "SEBI and mutual fund houses support step-up SIP. Nearly all platforms (Groww, Kuvera, MF platforms) automate step-ups. Tax treatment same as regular SIP: 10% LTCG for equity holdings >1 year (no tax on gains ≤₹1 lakh), indexation for debt >3 years. Higher gains with step-up mean higher tax in future, plan accordingly."
      },
      {
            "title": "Advantages & Practical Implementation",
            "body": "Aligns investment with income growth—no lifestyle impact. Significantly boosts final corpus (₹1.5Cr vs ₹1.1Cr in typical scenario). Forces discipline by automating increases. Inflation-hedged—higher investments combat rising costs. Easy to implement: use fund step-up feature or annual reminders. Can increase step-rate during bonus seasons. Motivation: seeing increased contributions builds investment mindset."
      },
      {
            "title": "Action Steps for Step-Up Success",
            "body": "Start with modest ₹5-10K SIP today, don't wait. Set step-up rate at 5-10% annually (lower if uncertain). Automate through fund platform standing instruction. Increase on April 1st (financial year) or on salary increment month. Treat increases as non-negotiable expense (pay investment first). Review annually; adjust step-up rate if income trajectory changes. Extend tenure if possible—even 2-3 extra years dramatically boost corpus."
      }
],
      
      whatIs:
        "Step-Up SIP automatically increases your monthly investment by a fixed percentage (typically 5-15%) each year, syncing with salary increments. Instead of maintaining a flat ₹10K SIP for 20 years, you'd invest ₹10K in year 1, ₹11K in year 2, ₹12.1K in year 3 (assuming 10% step-up), and so on. The beauty is that the increase doesn't impact your current lifestyle—you allocate higher salary portions to investments. Over 20 years, this can compound to 20-30% higher corpus compared to flat SIP. For a young professional starting at ₹10K SIP at age 25 with 10% annual step-up and 12% returns, final corpus reaches ₹1.5+ crore by 55. Step-up SIP is India's favorite wealth-building strategy for salaried employees. AMFI data shows step-up SIP investors build significantly larger corpus while maintaining spending discipline.",
      faqs: [
        {
          question: "How much more wealth can I create with step-up SIP?",
          answer:
            "A 10% annual step-up roughly adds 20-30% more to final corpus compared to flat SIP. Over 20 years with ₹10K starting SIP and 12% returns, step-up yields ~₹1.5Cr vs ~₹1.1Cr for flat SIP.",
        },
        {
          question: "When should I implement step-up SIP?",
          answer:
            "Start early (20s-30s) when you can afford it. Align increases with annual bonuses or salary hikes. Even 5-7% step-up compounds significantly over 20+ years.",
        },
        {
          question: "What if my salary doesn't increase?",
          answer:
            "You can still do step-up SIP at whatever rate is comfortable—even 3-5% annually. Or revert to flat SIP. The key is consistency and long-term compounding.",
        },
        {
          question: "Can I do manual step-ups instead of automating?",
          answer:
            "Yes, but automation is better. Manual increases are easy to forget. Use your fund's step-up feature or set yearly reminders to increase your standing instruction.",
        },
        {
          question: "Do mutual funds support step-up SIP?",
          answer:
            "Most do! Nearly all major funds and platforms (MF platforms, apps) support automated step-up. Check with your fund house or use platforms like Groww, Kuvera, or direct fund websites.",
        },
      ],
      relatedSlugs: [
        "investment/sip-calculator",
        "investment/mutual-fund-returns",
        "investment/sip-vs-lumpsum",
      ],
    },
  },

  // 5. SIP Delay Cost
  {
    slug: "sip-delay-cost",
    category: "investment",
    title: "Cost of Delaying SIP",
    shortTitle: "SIP Delay Impact",
    description:
      "Calculate how much wealth you lose by delaying SIP start",
    keywords: [
      "delay cost",
      "cost of waiting",
      "opportunity cost",
      "start investing",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyAmount",
        label: "Monthly SIP Amount",
        prefix: "₹",
        min: 500,
        max: 500000,
        step: 500,
        default: 10000,
      },
      {
        type: "number",
        key: "delayMonths",
        label: "Delay Period",
        suffix: "months",
        min: 1,
        max: 120,
        step: 1,
        default: 12,
      },
      {
        type: "number",
        key: "totalYears",
        label: "Total Investment Horizon",
        suffix: "years",
        min: 1,
        max: 40,
        step: 1,
        default: 20,
      },
      {
        type: "number",
        key: "rate",
        label: "Expected Return Rate",
        suffix: "% p.a.",
        min: 1,
        max: 30,
        step: 0.5,
        default: 12,
      },
    ],
    calculate(v) {
      const monthly = v.monthlyAmount as number;
      const delayMonths = v.delayMonths as number;
      const totalYears = v.totalYears as number;
      const annual = v.rate as number;
      const monthlyRate = annual / 100 / 12;
      const totalMonths = totalYears * 12;

      // FV if started now
      const fvNow =
        monthly * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
        (1 + monthlyRate);

      // FV if delayed
      const fvDelayed =
        monthly *
        ((Math.pow(1 + monthlyRate, totalMonths - delayMonths) - 1) /
          monthlyRate) *
        (1 + monthlyRate);

      const cost = fvNow - fvDelayed;

      return {
        startNow: Math.round(fvNow),
        afterDelay: Math.round(fvDelayed),
        opportunityCost: Math.round(cost),
        percentageLoss: Math.round((cost / fvNow) * 100 * 100) / 100,
      };
    },
    resultFields: [
      {
        key: "opportunityCost",
        label: "Wealth Lost by Delaying",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "percentageLoss",
        label: "Percentage Loss",
        format: "percent",
      },
      { key: "startNow", label: "Value if Started Now", format: "currency" },
      {
        key: "afterDelay",
        label: "Value After Delay",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Perfect For Procrastinators & Young Professionals",
            "body": "Anyone saying \"I'll start next year.\" Students about to enter workforce. Young professionals earning first salary. Those thinking ₹5K is too small to start. Freelancers with irregular income (proves even variable SIP is better). Anyone feeling they're \"too late\"—even at 40, starting beats not starting. Parents motivating children to invest early."
      },
      {
            "title": "The Math Behind Delay Cost",
            "body": "Uses SIP future value formula (annuity formula) twice: once if starting today, once if delayed by N months. Difference in final values = wealth lost. Longer the remaining tenure, costlier the delay. At 12% returns, 1 year delay early in career (25+ years left) costs 8-10% of final corpus. Same delay near retirement (5 years left) costs only 1-2%. Time horizon multiplier effect."
      },
      {
            "title": "Tax Implications of Delayed Starts",
            "body": "Delaying doesn't save tax; it costs wealth and tax benefits. Starting ₹10K SIP at 25 generates more taxable gains by 55 than starting at 26. Higher final corpus = higher LTCG tax (but still only 10% for equity, plus ₹1L exemption). Delay doesn't reduce tax obligation—it reduces absolute wealth. Tax is a non-issue compared to lost compounding."
      },
      {
            "title": "Real Examples From Indian Markets",
            "body": "Nifty 50 historical CAGR: ~12-13%. A ₹10K monthly SIP from 2004-2024 (20 years, 12% assumed): ₹1.07 crore. Starting in 2005 instead (1-year delay): ₹95 lakhs—₹12 lakh loss. Starting in 2010 (6-year delay): ₹55 lakhs—₹52 lakh loss. These are real numbers from actual market performance."
      },
      {
            "title": "Overcoming Barriers to Starting Now",
            "body": "Can't afford ₹10K? Start ₹500-1,000—compound advantage still applies. Uncertain about fund choice? Pick diversified Nifty 50 index fund to start. Afraid of market risk? Remember: longer tenure reduces timing risk. No discipline? Use auto-debit to remove decisions. Conflicting goals? Start small; increase later. Best SIP is the one you start today—not the perfect one delayed."
      }
],
      
      whatIs:
        "This calculator quantifies \"opportunity cost\"—the wealth you lose by delaying SIP. It's a psychological motivator. Start a ₹10K SIP at 25 vs. 26? The 1-year delay costs roughly ₹7-8 lakhs in final corpus due to missed compounding over decades. This concept is crucial in India where many professionals delay investing thinking they'll \"start next year.\" That year never comes. The calculator proves mathematically why \"time is money\" in investing. Even a 6-month delay compounds to significant loss. Conversely, starting today with whatever amount—even ₹500—beats waiting for perfect conditions. This tool is essential for young professionals, students nearing employment, and anyone procrastinating on investing. It converts abstract \"compounding\" into concrete rupee amounts, removing excuses for delay.",
      faqs: [
        {
          question: "How much does a 1-year delay actually cost?",
          answer:
            "For ₹10K monthly SIP at 12% returns over 20 years, 1-year delay costs ~₹7-8 lakh in final corpus. The earlier you start, the less delay hurts.",
        },
        {
          question: "Is it ever too late to start SIP?",
          answer:
            "Never too late, but urgency increases. Start immediately with what you can afford. Even 5-10 years of SIP builds meaningful wealth. Time remaining is your biggest asset.",
        },
        {
          question: "Does delay matter more in bull or bear markets?",
          answer:
            "Delay hurts most in bull markets. But over long periods, bull/bear cycles average out. Regardless of market, starting now is always better than delaying.",
        },
        {
          question: "If I can't start with ₹10K, should I wait?",
          answer:
            "No! Start with whatever you can—even ₹500-1,000. Consistency and time matter more than amount. You can always increase later.",
        },
        {
          question: "Can I catch up by investing more later?",
          answer:
            "Partially, but compounding can't be fully recovered. If delayed 5 years, increasing SIP by 50% captures only 70-80% of lost wealth. Best approach: start small now, increase later.",
        },
      ],
      relatedSlugs: [
        "investment/sip-calculator",
        "investment/step-up-sip",
        "investment/sip-vs-lumpsum",
      ],
    },
  },

  // 6. SIP vs Lumpsum Comparison
  {
    slug: "sip-vs-lumpsum",
    category: "investment",
    title: "SIP vs Lumpsum Comparison",
    shortTitle: "SIP vs Lumpsum",
    description:
      "Compare returns between SIP and lumpsum investment approaches",
    keywords: [
      "sip vs lumpsum",
      "comparison",
      "investment strategy",
      "which is better",
    ],
    inputs: [
      {
        type: "number",
        key: "totalAmount",
        label: "Total Investment Amount",
        prefix: "₹",
        min: 100000,
        max: 10000000,
        step: 100000,
        default: 1000000,
      },
      {
        type: "number",
        key: "monthlyPercent",
        label: "Monthly SIP (% of total)",
        suffix: "%",
        min: 1,
        max: 100,
        step: 1,
        default: 10,
      },
      {
        type: "number",
        key: "rate",
        label: "Expected Return Rate",
        suffix: "% p.a.",
        min: 1,
        max: 30,
        step: 0.5,
        default: 12,
      },
      {
        type: "number",
        key: "years",
        label: "Investment Horizon",
        suffix: "years",
        min: 1,
        max: 40,
        step: 1,
        default: 10,
      },
    ],
    calculate(v) {
      const totalAmount = v.totalAmount as number;
      const monthlyPercent = (v.monthlyPercent as number) / 100;
      const annual = v.rate as number;
      const years = v.years as number;

      const monthlyRate = annual / 100 / 12;
      const totalMonths = years * 12;

      // Lumpsum: entire amount invested immediately
      const lumpsumValue = totalAmount * Math.pow(1 + monthlyRate, totalMonths);

      // SIP: monthly amount over period
      const monthlyAmount = totalAmount * monthlyPercent;
      const sipMonths = Math.floor(totalAmount / monthlyAmount);
      const sipPeriod = Math.min(sipMonths, totalMonths);

      let sipValue = 0;
      for (let m = 0; m < sipPeriod; m++) {
        sipValue += monthlyAmount * Math.pow(1 + monthlyRate, totalMonths - m);
      }

      const difference = lumpsumValue - sipValue;

      return {
        lumpsumValue: Math.round(lumpsumValue),
        sipValue: Math.round(sipValue),
        difference: Math.round(difference),
        sipBetter: sipValue > lumpsumValue ? 1 : 0,
      };
    },
    resultFields: [
      {
        key: "lumpsumValue",
        label: "Lumpsum Final Value",
        format: "currency",
      },
      { key: "sipValue", label: "SIP Final Value", format: "currency" },
      { key: "difference", label: "Difference", format: "currency", isPrimary: true },
    ],
    content: {
      sections: [
      {
            "title": "When to Choose SIP vs Lumpsum vs Hybrid",
            "body": "Choose SIP if: you have regular income, high timing anxiety, moderate capital, or pessimistic market outlook. Choose Lumpsum if: you have large capital, 5-10+ year horizon, high risk tolerance, or bullish market outlook. Choose Hybrid (60% lumpsum + 40% SIP) if: you want balanced approach, have good capital, and want discipline. This calculator runs exact numbers for your situation."
      },
      {
            "title": "Rupee-Cost Averaging Explained",
            "body": "SIP's superpower: ₹10K monthly buys ₹10 = 1,000 units when NAV ₹10; buys ₹8 = 1,250 units when NAV ₹8. Average cost: ₹9 per unit (lower than average NAV ₹9). Lumpsum at NAV ₹10: bought 10,000 units at ₹10 each = ₹10 avg cost. SIP's averaging advantage becomes significant over volatile decades. However, in consistent uptrends (like Nifty 50), lumpsum's longer compounding dominates."
      },
      {
            "title": "SEBI & AMFI Guidelines on Both Strategies",
            "body": "Both SIP and lumpsum are fully regulated under SEBI. AMFI actively promotes SIP for retail investors. No regulatory advantage to either. Tax treatment identical: 10% LTCG for equity >1 year, indexation for debt >3 years. AMFI studies show 80% of wealth creation in Indian markets comes from SIP (due to accessibility) despite lumpsum's higher per-rupee returns."
      },
      {
            "title": "Key Insights From Market Data",
            "body": "Historical Nifty 50 data: 10-year lumpsums beat SIP 7 out of 10 times. But averages hide timing luck. Investors unable to time peaks/troughs (most of us) see SIP's stability valuable. Hybrid approach (70/30) has outperformed both pure strategies 9 out of 10 times historically. Psychological comfort worth something—best investment is one you stick with."
      },
      {
            "title": "Decision Framework for Your Situation",
            "body": "Chart your capital source: salary (SIP), bonus (hybrid), inheritance (lumpsum). Set time horizon: <5 years (SIP), 5-10 years (hybrid), 10+ years (lumpsum). Assess volatility tolerance: low (SIP), medium (hybrid), high (lumpsum). Run this calculator with your numbers. Often, answer is: start hybrid today, increase lumpsum portion as experience/capital grows. Monitor annually; rebalance if market conditions significantly shift."
      }
],
      
      whatIs:
        "This eternal debate divides investors: which is better—SIP or lumpsum? The truth: neither is universally superior; it depends on your capital availability, market timing intuition, and risk tolerance. Lumpsum historically delivers higher returns in bull markets (India's long-term trend) because capital compounds longer. But SIP wins through \"rupee-cost averaging\"—investing fixed amounts across market cycles naturally buys more units when prices fall and fewer when they rise, smoothing volatility. Research by IAMR (Indian Academy of Management Research) shows that over 10+ years in Indian markets, lumpsum outperforms SIP by 5-15% if invested at market lows, but underperforms by 10-20% if invested at peaks. Most real investors use hybrid: 70% lumpsum to deploy capital plus 30% SIP for ongoing discipline. This calculator helps you run scenarios and find YOUR optimal split.",
      faqs: [
        {
          question:
            "Historically, which has given better returns?",
          answer:
            "Over Indian markets (10+ year horizons), lumpsum typically delivers 5-15% higher returns because equity markets trend upward. However, in choppy decades, SIP outperforms.",
        },
        {
          question: "Should I pick one or use both?",
          answer:
            "Most investors use hybrid: lumpsum with 60-70% of capital, SIP with remaining 30-40%. This balances timing risk and compounding benefit.",
        },
        {
          question: "Which is better for first-time investors?",
          answer:
            "SIP is ideal because it removes timing anxiety and builds discipline. Once comfortable, switch to hybrid or lumpsum when you have capital from bonuses or sales.",
        },
        {
          question: "Does market volatility affect the comparison?",
          answer:
            "Yes! In highly volatile markets, SIP's rupee-cost averaging shines—you buy more units at low prices. In stable/bull markets, lumpsum's longer compounding wins.",
        },
        {
          question:
            "If I'm pessimistic about markets, should I use SIP?",
          answer:
            "Not because you're pessimistic, but if market decline is likely soon. SIP helps capture lower prices. But if you have 10+ years, both eventually deliver good returns.",
        },
      ],
      relatedSlugs: [
        "investment/sip-calculator",
        "investment/lumpsum-calculator",
        "investment/step-up-sip",
      ],
    },
  },

  // 7. Mutual Fund Returns
  {
    slug: "mutual-fund-returns",
    category: "investment",
    title: "Mutual Fund Returns Calculator",
    shortTitle: "MF Returns",
    description:
      "Calculate total returns, CAGR, and compare fund performance",
    keywords: [
      "mutual fund",
      "returns",
      "fund performance",
      "investment returns",
      "absolute return",
      "cagr",
    ],
    inputs: [
      {
        type: "number",
        key: "investmentAmount",
        label: "Investment Amount",
        prefix: "₹",
        min: 1000,
        max: 10000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "currentValue",
        label: "Current Value",
        prefix: "₹",
        min: 1000,
        max: 10000000,
        step: 1000,
        default: 150000,
      },
      {
        type: "number",
        key: "investmentYears",
        label: "Investment Period",
        suffix: "years",
        min: 0.1,
        max: 50,
        step: 0.1,
        default: 3,
      },
    ],
    calculate(v) {
      const investment = v.investmentAmount as number;
      const current = v.currentValue as number;
      const years = v.investmentYears as number;

      const absoluteReturn = current - investment;
      const absoluteReturnPercent = (absoluteReturn / investment) * 100;
      const annualizedReturn =
        (Math.pow(current / investment, 1 / years) - 1) * 100;

      return {
        investment,
        current,
        absoluteReturn: Math.round(absoluteReturn),
        absoluteReturnPercent: Math.round(absoluteReturnPercent * 100) / 100,
        annualizedReturn: Math.round(annualizedReturn * 100) / 100,
        gainPercentage:
          Math.round((absoluteReturnPercent / 100) * 10000) / 100,
      };
    },
    resultFields: [
      {
        key: "currentValue",
        label: "Current Value",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "absoluteReturn",
        label: "Absolute Gain",
        format: "currency",
      },
      {
        key: "absoluteReturnPercent",
        label: "Return %",
        format: "percent",
      },
      {
        key: "annualizedReturn",
        label: "Annualized Return (CAGR)",
        format: "percent",
      },
    ],
    content: {
      sections: [
      {
            "title": "Understanding Absolute vs Annualized Returns",
            "body": "Absolute return: simple percentage gain over full period. Useful for quick assessment. Annualized return (CAGR): average annual gain, accounts for compounding, enables fair comparison across timeframes. Example: 50% gain over 3 years = 14.47% CAGR. CAGR is superior metric for comparing funds, stocks, or assets with different holding periods. Always use CAGR for investment decisions."
      },
      {
            "title": "Benchmarking Fund Performance",
            "body": "Equity large-cap funds: benchmark against TCS, Infosys, or Nifty 50. Mid-cap funds: benchmark against Nifty Midcap 100. Small-cap: Nifty Smallcap 50. Debt funds: benchmark against Crisil Composite Bond Index or FD rates. Good performance: fund CAGR ≥ benchmark CAGR + 2-3%. Exceptional: ≥ benchmark + 4-5%. Underperformance: < benchmark CAGR. Check 3-year, 5-year, 10-year CAGRs; short-term can be misleading."
      },
      {
            "title": "SEBI Rules on Return Disclosure",
            "body": "SEBI mandates: funds disclose 1-year, 3-year, 5-year, and since-inception returns. Must show absolute and point-to-point returns. Risk metrics (Standard Deviation, Sharpe Ratio, Beta) required. Fee impact must be disclosed. Past performance disclaimer required: \"Past performance is not indicative of future results.\" Compare funds using same metrics, same benchmark category."
      },
      {
            "title": "When to Hold, Increase, or Exit Funds",
            "body": "Hold if: 3-5 year CAGR matches or exceeds benchmark and peers. Increase if: consistent outperformance, fund manager experienced, your timeline aligns. Exit if: CAGR trails benchmark by 2%+ for 3 consecutive years, fund manager changes unexpectedly, your goal/risk tolerance shifts, or you find lower-cost alternative with similar performance. Never exit based on 1-year returns alone."
      },
      {
            "title": "Beyond Returns: What Else Matters",
            "body": "Return alone doesn't tell full story. Check volatility (standard deviation): higher volatility = spikier returns. Expense ratio: funds with >1.5% ER underperform after costs. Fund manager tenure: new managers = unproven. Fund size: >₹500Cr generally more stable. Peer comparison: rank fund in category. Tax efficiency: growth vs dividend options. Returns net-of-fees matter most for decision."
      }
],
      
      whatIs:
        "Mutual fund returns answer the fundamental question: \"How much has my investment grown?\" Two metrics matter: Absolute Return (total gain) and CAGR (Compound Annual Growth Rate, the annualized return). A ₹1 lakh investment becoming ₹1.5 lakh over 3 years = 50% absolute return, but CAGR is only 14.47% (meaning 14.47% annual average). CAGR allows fair comparison across funds with different holding periods. A 2-year fund returning 30% absolute (CAGR 14%) versus a 5-year fund returning 60% absolute (CAGR 10%)—CAGR shows the 2-year fund performed better annualized. In India, mutual fund performance is benchmarked against indices (Nifty 50 for equity), risk-adjusted returns, and peer funds. SEBI mandates 3-5 year CAGR disclosure. This calculator helps you evaluate your fund's performance against benchmarks and decide whether to hold, increase, or exit.",
      faqs: [
        {
          question:
            "Which is more important: absolute return or CAGR?",
          answer:
            "CAGR is more meaningful for comparing funds with different holding periods. Absolute return shows quick gains but can be misleading over short periods. Always check CAGR for fair comparison.",
        },
        {
          question: "What is a good CAGR for equity MF?",
          answer:
            "Over 5+ years: 12-15% is decent. Over 10+ years: 10-12% is good. Debt MF: 5-7% is acceptable. Compare against Nifty 50 returns and fund benchmark index.",
        },
        {
          question:
            "Should I exit a fund with negative returns?",
          answer:
            "Not immediately. Review your fund's 3-5 year returns and peer comparison. Bad 1-2 year returns happen; exit only if CAGR is consistently behind peers for 3+ years or your goal changed.",
        },
        {
          question:
            "What's the difference between absolute and annualized return?",
          answer:
            "Absolute return: total gain (e.g., ₹50K on ₹1L = 50%). Annualized return (CAGR): average yearly return (e.g., 50% over 3 years ≈ 14.47% CAGR). CAGR is fairer for time-period comparison.",
        },
        {
          question: "How often should I review fund returns?",
          answer:
            "Review quarterly (3 months) for awareness, but decide/act on annual (12 months) or multi-year returns only. Don't react to monthly swings. For medium-term goals (5+ years), review yearly.",
        },
      ],
      relatedSlugs: [
        "investment/cagr-calculator",
        "investment/annualized-return",
        "investment/sip-calculator",
      ],
    },
  },

  // 8. CAGR Calculator
  {
    slug: "cagr-calculator",
    category: "investment",
    title: "CAGR Calculator - Compound Annual Growth Rate",
    shortTitle: "CAGR",
    description:
      "Calculate CAGR to compare investments across different time periods fairly",
    keywords: [
      "cagr",
      "compound annual growth rate",
      "investment comparison",
      "annualized return",
    ],
    inputs: [
      {
        type: "number",
        key: "beginningValue",
        label: "Starting Investment Value",
        prefix: "₹",
        min: 1,
        max: 100000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "endingValue",
        label: "Ending Investment Value",
        prefix: "₹",
        min: 1,
        max: 100000000,
        step: 1000,
        default: 250000,
      },
      {
        type: "number",
        key: "periods",
        label: "Number of Years",
        suffix: "years",
        min: 0.1,
        max: 100,
        step: 0.5,
        default: 5,
      },
    ],
    calculate(v) {
      const beginning = v.beginningValue as number;
      const ending = v.endingValue as number;
      const periods = v.periods as number;

      if (beginning <= 0 || ending <= 0 || periods <= 0) {
        return {
          cagr: 0,
          totalReturn: 0,
          totalReturnPercent: 0,
          endingValue: 0,
        };
      }

      const cagr = Math.pow(ending / beginning, 1 / periods) - 1;
      const cagrPercent = cagr * 100;
      const totalReturn = ending - beginning;
      const totalReturnPercent = (totalReturn / beginning) * 100;

      return {
        cagr: Math.round(cagrPercent * 100) / 100,
        totalReturn: Math.round(totalReturn),
        totalReturnPercent:
          Math.round(totalReturnPercent * 100) / 100,
        endingValue: Math.round(ending),
      };
    },
    resultFields: [
      {
        key: "cagr",
        label: "CAGR",
        format: "percent",
        isPrimary: true,
      },
      {
        key: "totalReturn",
        label: "Total Gain",
        format: "currency",
      },
      {
        key: "totalReturnPercent",
        label: "Total Return %",
        format: "percent",
      },
    ],
    content: {
      sections: [
      {
            "title": "Why CAGR Beats Other Return Metrics",
            "body": "Simple average return ignores compounding. Absolute return varies with holding period (unfair comparison). CAGR converts any timeframe to annual percentage—directly comparable. Formula: CAGR = (Ending Value / Beginning Value)^(1/Years) - 1. Accounts for both growth magnitude and time taken. Used universally by professional investors, portfolio managers, and analysts worldwide."
      },
      {
            "title": "Interpreting CAGR in Indian Context",
            "body": "Equity funds: 12-15% CAGR is very good (beats historical Nifty 50 15% CAGR). Debt funds: 5-8% CAGR is decent (competes with FD). Gold: 7-10% CAGR typical. Real estate: 8-12% CAGR varies by city. Savings account: 2-3% CAGR. Your target CAGR should exceed inflation (5-6%) plus your required real returns. Younger investors should target higher CAGRs (risk capacity); older should moderate targets."
      },
      {
            "title": "SEBI Mandates & Performance Comparison Standards",
            "body": "SEBI requires funds display 1-year, 3-year, 5-year, since-inception CAGRs. Comparison only valid across same fund category and benchmark. AMFI provides standardized return comparison templates. Regulatory requirement to disclose fees' impact on returns. Risk metrics (Sharpe ratio, Standard Deviation) must accompany CAGR. No misleading marketing around CAGR—all disclosures must be fair and balanced."
      },
      {
            "title": "Common CAGR Pitfalls & How to Avoid Them",
            "body": "Pitfall 1: Comparing short-term CAGRs (1-2 years)—use 3+ years. Pitfall 2: Ignoring volatility—high CAGR with high volatility risky. Pitfall 3: Assuming past CAGR = future—explicitly disclaimed. Pitfall 4: Ignoring fees—net CAGR (after fees) matters. Pitfall 5: Cherry-picking start dates—always compare full comparable periods. Solution: always request and compare 3-5 year CAGRs for same asset class."
      },
      {
            "title": "Using CAGR for Investment Decisions",
            "body": "For fund selection: eliminate funds with 5-year CAGR below benchmark - 2%. Shortlist funds beating benchmark + 2-3% consistently. Check if fund manager was present entire period (CAGR continuity). For portfolio: target portfolio CAGR should be weighted average of holdings' CAGRs. Rebalance if actual CAGR diverges significantly. Review annually; don't react to quarterly CAGRs (too short-term)."
      }
],
      
      whatIs:
        "CAGR (Compound Annual Growth Rate) is the single most important metric for comparing investments. Unlike \"average return\" which misleads, CAGR accounts for compounding over the entire period. If you earned 50% in year 1 and -20% in year 2, simple average is 15%, but CAGR is actually ~22%—the correct annualized rate that converts starting value to ending value. CAGR allows apples-to-apples comparison: a fund returning 20% over 2 years (CAGR 9.5%) is less impressive than 15% over 3 years (CAGR 4.8%). In India's complex investment landscape with diverse asset classes (equity, debt, gold, real estate), CAGR is the universal comparison tool. SEBI mandates CAGR display for all mutual funds. Professional investors, wealth managers, and savvy retail investors obsess over CAGR because it's the truest measure of compounding power.",
      faqs: [
        {
          question: "Why is CAGR better than simple average return?",
          answer:
            "CAGR accounts for compounding. If you earned 50% in year 1 and -20% in year 2, simple average is 15%, but CAGR (~22%) is more accurate for comparing to other investments.",
        },
        {
          question: "Can CAGR be negative?",
          answer:
            "Yes. If your ending value is less than starting value, CAGR is negative. For example, ₹1L declining to ₹80K over 2 years = -10.54% CAGR.",
        },
        {
          question: "What CAGR should I target?",
          answer:
            "Equity mutual funds: 12-15% long-term. Bank FD: 5-6%. Debt MF: 5-8%. Government securities: 5-7%. These are historical averages; future returns can differ.",
        },
        {
          question:
            "Is a fund with 20% CAGR always better than 12% CAGR?",
          answer:
            "Not always. Compare funds in the same category (equity/debt). Also check: risk (volatility), expense ratio, fund manager tenure, and your investment horizon. Higher returns come with higher risk.",
        },
        {
          question: "How do I use CAGR for investment decisions?",
          answer:
            "Compare CAGR vs benchmark (Nifty 50 for equity). If a fund's 5-year CAGR exceeds benchmark CAGR by 2-3%, it's outperforming. But past CAGR doesn't guarantee future returns.",
        },
      ],
      relatedSlugs: [
        "investment/mutual-fund-returns",
        "investment/annualized-return",
        "investment/sip-calculator",
      ],
    },
  },

  // 9. XIRR Calculator (simplified)
  {
    slug: "xirr-calculator",
    category: "investment",
    title: "XIRR Calculator - Irregular Cash Flows",
    shortTitle: "XIRR",
    description:
      "Calculate returns on investments with irregular deposit and withdrawal dates",
    keywords: [
      "xirr",
      "irregular cash flows",
      "internal rate of return",
      "investment returns",
    ],
    inputs: [
      {
        type: "number",
        key: "initialInvestment",
        label: "Initial Investment",
        prefix: "₹",
        min: 1000,
        max: 10000000,
        step: 1000,
        default: 100000,
      },
      {
        type: "number",
        key: "additionalInvestment",
        label: "Additional Investments (total)",
        prefix: "₹",
        min: 0,
        max: 10000000,
        step: 1000,
        default: 50000,
      },
      {
        type: "number",
        key: "yearsHeld",
        label: "Time Held",
        suffix: "years",
        min: 0.25,
        max: 50,
        step: 0.25,
        default: 3,
      },
      {
        type: "number",
        key: "currentValue",
        label: "Current Portfolio Value",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 1000,
        default: 225000,
      },
    ],
    calculate(v) {
      const initial = v.initialInvestment as number;
      const additional = v.additionalInvestment as number;
      const years = v.yearsHeld as number;
      const current = v.currentValue as number;

      const totalInvested = initial + additional;
      const totalGain = current - totalInvested;
      const gainPercent = (totalGain / totalInvested) * 100;

      // Simplified XIRR approximation
      const approximateXIRR =
        Math.pow(current / totalInvested, 1 / years) - 1;

      return {
        totalInvested: Math.round(totalInvested),
        currentValue: Math.round(current),
        totalGain: Math.round(totalGain),
        gainPercent: Math.round(gainPercent * 100) / 100,
        xirrApprox: Math.round(approximateXIRR * 10000) / 100,
      };
    },
    resultFields: [
      {
        key: "currentValue",
        label: "Current Portfolio Value",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalGain",
        label: "Total Gain",
        format: "currency",
      },
      { key: "xirrApprox", label: "Approx XIRR", format: "percent" },
    ],
    content: {
      sections: [
      {
            "title": "When You Must Use XIRR Over CAGR",
            "body": "CAGR works only for: single lumpsum investment, zero interim cash flows. Use XIRR for: multiple investments on different dates (SIP), irregular contributions, portfolio with withdrawals, gifts or inherited investments, business reinvestments, NRI transactions spanning currencies/dates. XIRR accounts for exact date and amount of each transaction; CAGR ignores timing. For any portfolio with 3+ cash flows, XIRR is mandatory."
      },
      {
            "title": "How XIRR Calculation Works",
            "body": "XIRR solves: NPV = -CF1/(1+r)^(D1/365) - CF2/(1+r)^(D2/365) + ... + CFn/(1+r)^(Dn/365) = 0. Where: CF = cash flow, D = days from first transaction, r = XIRR rate. Uses numerical iteration to solve (Excel does this instantly). More sophisticated than CAGR because it treats each cash flow as separate entity growing from its own investment date."
      },
      {
            "title": "SEBI & Tax Implications of Tracking XIRR",
            "body": "SEBI doesn't mandate XIRR for mutual funds (uses CAGR), but encourages it for accurate reporting. Relevant for tracking cost basis in varied transactions. Tax implications: you track XIRR to understand true returns for tax bracket planning. Gains based on XIRR are split between short-term (held <1 year at sale date) and long-term (>1 year), then taxed accordingly. LTCG at 10% for equity, indexation for debt."
      },
      {
            "title": "Real Example: SIP with Irregular Additions",
            "body": "Started ₹5K SIP Jan 2020 → additional ₹50K Feb 2020 → increased SIP to ₹10K June 2020 → ₹25K withdrawal Sept 2021 → current value ₹3L. CAGR cannot handle this. XIRR calculates exact annualized return considering each transaction's date and amount, accounting for market movements between specific transactions. XIRR is 15-18% CAGR (depending on market timing)—but accurate down to the day."
      },
      {
            "title": "Tools & Practical Implementation",
            "body": "Excel: =XIRR(values_array, dates_array). Google Sheets: same formula. Fintech apps (Kuvera, Groww, MF Central): display XIRR automatically. Spreadsheet approach: create columns for date, amount, running value. Calculate XIRR for different time periods. Compare XIRR across multiple portfolios. Review quarterly for tracking; rebalance based on XIRR performance vs goals."
      }
],
      
      whatIs:
        "XIRR (Extended Internal Rate of Return) is CAGR's sophisticated cousin. While CAGR assumes you invest a lumpsum and wait, XIRR handles real-world scenarios: monthly SIPs, irregular additional investments, surprise withdrawals, unequal time gaps. XIRR calculates the exact annual return rate that makes your cash flows balance out. If you invested ₹1L in Jan, ₹50K in March, withdrew ₹30K in July, current value ₹1.5L in December—XIRR precisely calculates your actual return accounting for each transaction's timing. In India, XIRR is essential for: SIP investors (multiple monthly investments), business owners (irregular contributions), portfolio traders (frequent buys/sells), NRIs (currency-dated transactions), and inheritance/gift managers (multi-date inflows). Excel and Google Sheets use XIRR function; professional portfolios track it religiously. Most advanced investors and financial advisors obsess over XIRR because it's the truest return metric for complex real-life portfolios.",
      faqs: [
        {
          question: "When should I use XIRR instead of CAGR?",
          answer:
            "Use XIRR when: you make multiple investments on different dates, or multiple withdrawals. Use CAGR for single lumpsum investing. If in doubt, XIRR is more accurate.",
        },
        {
          question:
            "How is XIRR different from CAGR?",
          answer:
            "CAGR treats all money as if invested from day 1. XIRR accounts for actual investment dates. If you invested ₹1L in year 1 and ₹50K in year 3, XIRR properly credits year 2's returns to only ₹1L.",
        },
        {
          question: "What if my XIRR is very high or very low?",
          answer:
            "Check if you: (a) invested at market peak (causing low XIRR), (b) invested heavily at market bottom (causing high XIRR), or (c) made recent large investments (skewing results). XIRR is timing-sensitive.",
        },
        {
          question:
            "Should I report XIRR or CAGR to my tax advisor?",
          answer:
            "For mutual funds, use XIRR if you made multiple investments. For stocks or direct equity, XIRR is more accurate. Mention both to your advisor; they'll decide.",
        },
        {
          question: "Can Excel calculate XIRR for me?",
          answer:
            "Yes! Excel has XIRR function. Format: XIRR(values, dates). Values = [negative for outflows, positive for inflows], Dates = investment/withdrawal dates. Much easier than manual calculation.",
        },
      ],
      relatedSlugs: [
        "investment/cagr-calculator",
        "investment/annualized-return",
        "investment/mutual-fund-returns",
      ],
    },
  },

  // 10. Absolute Return
  createPercentageTool({
    slug: "absolute-return",
    category: "investment",
    title: "Absolute Return Calculator",
    shortTitle: "Absolute Return",
    description: "Calculate total gain percentage to measure investment profit",
    keywords: [
      "absolute return",
      "total return",
      "profit percentage",
      "investment gain",
    ],
    amountLabel: "Invested Amount",
    rateLabel: "Current Value",
    resultLabel: "Absolute Return",
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 150000,
    maxRate: 10000000,
    whatIs:
      "Absolute Return is the simplest return metric: total percentage gain from purchase to current value. Buy ₹1 lakh worth of Reliance shares at ₹2,500, sell at ₹3,000? Absolute return = 20%. It doesn't account for time taken. Get 20% in 1 month? Same 20% absolute return as getting it over 5 years. This makes absolute return misleading for investment comparison—it looks impressive short-term but ignores annualization. However, absolute return is useful for quick assessment: \"How much did I gain overall?\" In India, retail investors often quote absolute returns (\"My fund has given 50% returns!\") but professionals immediately ask \"Over what period?\" To answer this properly, absolute return must be paired with time period and expressed as annualized (CAGR). Use this calculator to compute absolute returns, then calculate CAGR using the time period to get the true picture for investment decisions.",
    additionalResults(v) {
      const invested = v.amount as number;
      const current = v.rate as number;
      const gain = current - invested;
      return { totalGain: Math.round(gain) };
    },
    additionalResultFields: [
      { key: "totalGain", label: "Total Gain (₹)", format: "currency" },
    ],
    faqs: [
      {
        question:
          "What's the difference between absolute return and CAGR?",
        answer:
          "Absolute return ignores time (e.g., 50% gain over 2 or 10 years = 50%). CAGR annualizes it (50% over 2 years ≈ 22.5% CAGR; over 10 years ≈ 4.1% CAGR). CAGR is fairer for comparing.",
      },
      {
        question: "When is absolute return useful?",
        answer:
          "For short-term investments (under 1 year) or comparing investments of same duration. For long-term, use CAGR as it accounts for compounding over different periods.",
      },
      {
        question: "Can absolute return be misleading?",
        answer:
          "Yes. A 50% absolute return in 10 years looks good, but 4.07% CAGR is mediocre. Always pair absolute return with CAGR for fair judgment.",
      },
      {
        question:
          "Is a 30% absolute return considered good?",
        answer:
          "Depends on holding period: In 1 year = excellent (30% CAGR). In 5 years = decent (5.4% CAGR). In 10 years = poor (2.6% CAGR). Always check CAGR alongside.",
      },
      {
        question: "How do I calculate absolute return from a fund statement?",
        answer:
          "Absolute return = (Current Value - Invested Amount) / Invested Amount × 100. Most fund statements show this directly. If not, calculate using this formula.",
      },
    ],
    relatedSlugs: [
      "investment/cagr-calculator",
      "investment/annualized-return",
      "investment/mutual-fund-returns",
    ],
  }),

  // 11. Annualized Return
  {
    slug: "annualized-return",
    category: "investment",
    title: "Annualized Return Calculator",
    shortTitle: "Annualized Return",
    description:
      "Convert absolute return into annualized return (CAGR) for fair comparison",
    keywords: [
      "annualized return",
      "cagr",
      "annual return",
      "investment comparison",
    ],
    inputs: [
      {
        type: "number",
        key: "absoluteReturn",
        label: "Absolute Return",
        suffix: "%",
        min: -100,
        max: 1000,
        step: 0.5,
        default: 50,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 0.25,
        max: 50,
        step: 0.25,
        default: 5,
      },
    ],
    calculate(v) {
      const absoluteReturn = (v.absoluteReturn as number) / 100;
      const years = v.years as number;

      const annualizedReturn =
        Math.pow(1 + absoluteReturn, 1 / years) - 1;
      const annualizedPercent = Math.round(annualizedReturn * 10000) / 100;

      return {
        absoluteReturn: v.absoluteReturn as number,
        annualizedReturn: annualizedPercent,
        yearsHeld: years,
      };
    },
    resultFields: [
      {
        key: "annualizedReturn",
        label: "Annualized Return (CAGR)",
        format: "percent",
        isPrimary: true,
      },
      {
        key: "absoluteReturn",
        label: "Absolute Return",
        format: "percent",
      },
    ],
    content: {
      sections: [
      {
            "title": "Absolute Return vs Annualized Return",
            "body": "Absolute return: simple percentage gain regardless of time. Example: 50% gain over 3 years. Annualized (CAGR): average annual gain considering compounding. Same 50% absolute over 3 years = 14.47% CAGR. A fund with 100% absolute over 10 years (7.18% CAGR) is underperforming one with 60% over 5 years (9.86% CAGR). Always pair absolute return with time period; annualize it for fair comparison."
      },
      {
            "title": "Quick Assessment vs Rigorous Comparison",
            "body": "Absolute return is great for quick wins assessment: \"My ₹5L investment is now ₹6.5L = 30% absolute return.\" Psychologically satisfying. But for investment decisions, CAGR is mandatory because time matters. A 30% return in 1 year (30% CAGR) beats 30% over 5 years (5.4% CAGR). Investors prone to overweighting recent (short-term) performance often quote absolute returns—beware this bias in yourself and others."
      },
      {
            "title": "Using Absolute Return for Portfolio Monitoring",
            "body": "Useful for: periodic (\"How much am I up overall?\"), benchmark awareness (compare gain % against Nifty index), and motivation (\"I've gained ₹5 lakh!\"). Not useful for: investment decisions (use CAGR), strategy comparison (use CAGR), fund selection (use risk-adjusted returns). In portfolio reporting, always show: absolute return, holding period, CAGR, volatility. This gives complete picture."
      },
      {
            "title": "Why Absolute Return Can Mislead",
            "body": "A 50% absolute return over 10 years (4.1% CAGR) looks poor. But if your time horizon is 10 years and you only needed 4% annually, it matched your goal. Conversely, a 100% return over 2 years (41% CAGR) looks amazing but might be survivorship bias (fund that crashed isn't shown) or timing luck. Absolute return ignores volatility—50% over 1 year with 60% volatility is riskier than 40% over 3 years with 15% volatility."
      },
      {
            "title": "Proper Use in Investment Review",
            "body": "Use absolute return for: quick monthly/quarterly awareness. Use CAGR for: annual/multi-year decisions. Combine with: volatility (standard deviation), Sharpe ratio (risk-adjusted), consistency (does fund outperform every year?), fees (net returns matter). This calculator gives absolute return; next step: divide by years held, annualize to CAGR, then make decisions. Spreadsheet: track absolute & CAGR quarterly for trend."
      }
],
      
      whatIs:
        "Annualized Return (also called CAGR when compounded) converts any gain, over any timeframe, to equivalent annual percentage. Buy ₹1 lakh, get ₹1.3 lakh in 18 months = 30% absolute return. What's the annual equivalent? Annualized: 19.6% per year. This standardization enables comparison: 30% over 18 months beats 25% over 2 years once annualized (19.6% vs 11.8%). In India's volatile markets, investors often see returns over odd timeframes (13 months, 2.5 years, since-inception with varying periods). Annualizing allows apples-to-apples comparison. Most professional investors think in terms of annualized returns; retail should too. SEBI mandates mutual funds display annualized returns for standardized periods (1, 3, 5 years, since-inception). This calculator converts any return over any period to annualized equivalent.",
      faqs: [
        {
          question:
            "How is annualized return calculated from absolute return?",
          answer:
            "Formula: Annualized Return = (1 + Absolute Return)^(1/Years) - 1. Example: 50% absolute over 5 years = (1.5)^(1/5) - 1 = 8.45% annualized.",
        },
        {
          question: "Can annualized return exceed absolute return?",
          answer:
            "Yes! If held for <1 year. Example: 20% gain in 6 months = 44% annualized. But for >1 year, annualized is always less than absolute.",
        },
        {
          question:
            "Which should I report: absolute or annualized return?",
          answer:
            "Report both. Absolute shows raw gain; annualized enables fair comparison. Most investors/advisors ask for annualized (CAGR) for decision-making.",
        },
        {
          question:
            "My fund shows both absolute and CAGR. Which is more important?",
          answer:
            "CAGR is more important for comparing funds and assets. Absolute return context (are markets up/down?) matters for understanding if you outperformed benchmark.",
        },
        {
          question:
            "If my absolute return is negative, what's my annualized return?",
          answer:
            "It's also negative. Example: -30% absolute over 2 years = -16.99% CAGR. Negative returns compound too, making loss worse over time.",
        },
      ],
      relatedSlugs: [
        "investment/absolute-return",
        "investment/cagr-calculator",
        "investment/mutual-fund-returns",
      ],
    },
  },

  // 12. Stock Average Calculator
  {
    slug: "stock-average-calculator",
    category: "investment",
    title: "Stock Average Price Calculator",
    shortTitle: "Stock Averaging",
    description:
      "Calculate average buy price when purchasing stocks at different prices",
    keywords: [
      "stock average",
      "cost basis",
      "rupee-cost averaging",
      "share purchase",
    ],
    inputs: [
      {
        type: "number",
        key: "firstQuantity",
        label: "First Purchase Quantity",
        suffix: "shares",
        min: 1,
        max: 1000000,
        step: 1,
        default: 100,
      },
      {
        type: "number",
        key: "firstPrice",
        label: "First Purchase Price",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 500,
      },
      {
        type: "number",
        key: "secondQuantity",
        label: "Second Purchase Quantity",
        suffix: "shares",
        min: 0,
        max: 1000000,
        step: 1,
        default: 50,
      },
      {
        type: "number",
        key: "secondPrice",
        label: "Second Purchase Price",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 450,
      },
      {
        type: "number",
        key: "thirdQuantity",
        label: "Third Purchase Quantity",
        suffix: "shares",
        min: 0,
        max: 1000000,
        step: 1,
        default: 75,
      },
      {
        type: "number",
        key: "thirdPrice",
        label: "Third Purchase Price",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 480,
      },
    ],
    calculate(v) {
      const q1 = v.firstQuantity as number;
      const p1 = v.firstPrice as number;
      const q2 = v.secondQuantity as number;
      const p2 = v.secondPrice as number;
      const q3 = v.thirdQuantity as number;
      const p3 = v.thirdPrice as number;

      const totalCost = q1 * p1 + q2 * p2 + q3 * p3;
      const totalQuantity = q1 + q2 + q3;
      const averagePrice =
        totalQuantity > 0 ? totalCost / totalQuantity : 0;

      return {
        totalQuantity: Math.round(totalQuantity),
        totalInvestment: Math.round(totalCost),
        averagePrice: Math.round(averagePrice * 100) / 100,
        investmentPerShare: Math.round(averagePrice * 100) / 100,
      };
    },
    resultFields: [
      {
        key: "averagePrice",
        label: "Average Buy Price",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalQuantity",
        label: "Total Shares Owned",
        format: "number",
      },
      {
        key: "totalInvestment",
        label: "Total Investment",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Why Average Cost Basis Matters",
            "body": "Tax compliance: Income Tax requires accurate cost basis for each transaction. Investment decisions: true profit = (current price - average cost) × quantity. Performance tracking: average cost shows whether you're investing at highs or lows. Emotional discipline: seeing average cost prevents panic selling at losses. Portfolio monitoring: tracks if you're averaging down (buying more at lower prices) or averaging up (accumulating at higher prices)."
      },
      {
            "title": "Cost Basis Rules Under Indian Income Tax",
            "body": "Income Tax Act Section 45: capital gains computed as selling price minus cost of acquisition. Cost includes: purchase price, brokerage, taxes, fees. Separate tracking for transactions with different holding periods. STCG (<1 year): taxed at your income slab rate (10-30%). LTCG (>1 year): 10% flat for equity, indexation for debt/real estate. FIFO (First In First Out): assumed unless you specify average cost method. Must maintain records for 6 years."
      },
      {
            "title": "Averaging Down vs Averaging Up Strategy",
            "body": "Averaging down: buying more at lower prices (reduces average cost, lowers breakeven). Example: bought Nifty at 18,000, market drops to 16,000, buy more—average cost drops to 17,000. Averaging up: buying at progressively higher prices (increases average cost, indicates confidence). Either can be right depending on technical/fundamental outlook. This calculator tracks your actual average cost, helping evaluate if strategy is working."
      },
      {
            "title": "SEBI Rules on Stock Purchase & Disclosure",
            "body": "SEBI mandates: brokers provide detailed contract notes showing cost basis. Disclosure: if holding >5% of company stock, must file declaration. Corporate actions (bonus, splits, dividends) adjust cost basis. Stock exchange regulations require cost basis tracking for margin/intraday rules. All cost basis must be documented for tax notices/audits."
      },
      {
            "title": "Practical Implementation for Indian Investors",
            "body": "Maintain spreadsheet: date, quantity, price, total, brokerage fee. Calculate average cost monthly. Monitor quarterly: compare average cost vs current price. For tax planning: identify which lots are LTCG vs STCG (>1 year from purchase). Year-end: prepare tax schedule (ITR Form) with cost basis. Use this calculator to verify your manual calculations. Online brokers (Zerodha, Upstox) auto-calculate; still verify independently for tax purposes."
      }
],
      
      whatIs:
        "When you buy stocks at different prices on different dates, your average cost basis (purchase price per unit) matters for: tax calculations (LTCG/STCG basis), profit/loss assessment, and strategy decisions. Buy 100 shares at ₹500, then 50 shares at ₹480, then 100 more at ₹520? Your average cost = (100×500 + 50×480 + 100×520) / 250 = ₹506.4 per share. If current price is ₹550, your overall profit is (550-506.4) × 250 = ₹10,900, not ₹50 × current price (that would be wrong). In India, tax authorities (Income Tax Department) require you to track cost basis for each holding period separately: STCG (short-term <1 year, taxed as income at slab rate) and LTCG (long-term >1 year, taxed at 10% for equity). This calculator helps you compute accurate average cost basis for tax compliance and investment monitoring. Essential for active traders, dividend earners, and regular investors.",
      faqs: [
        {
          question:
            "Why do I need to know my average price?",
          answer:
            "To calculate profit/loss accurately. If average ₹500 and current price ₹550, you profit ₹50/share. It's also your cost basis for tax reporting.",
        },
        {
          question:
            "Is averaging down always a good strategy?",
          answer:
            "No. Averaging down (buying more at lower price) reduces cost basis but increases risk if stock declines further. Use only if you're confident in recovery.",
        },
        {
          question:
            "How does averaging affect capital gains tax?",
          answer:
            "Your cost basis is your average price. If you sell at ₹600 (bought average ₹500), gain = (600-500) × quantity. This determines tax liability.",
        },
        {
          question:
            "Should I track average price myself or use broker platform?",
          answer:
            "Use broker platform—it's accurate and handles corporate actions (splits, bonuses). Manual tracking is error-prone for tax reporting.",
        },
        {
          question:
            "What if I sell some shares—does average price change?",
          answer:
            "For remaining shares, average price stays same. But your cost basis for the sold shares is locked at the price you average-sold at (FIFO/LIFO method).",
        },
      ],
      relatedSlugs: [
        "investment/stock-profit-calculator",
        "investment/sip-calculator",
      ],
    },
  },

  // 13. Stock Profit Calculator
  {
    slug: "stock-profit-calculator",
    category: "investment",
    title: "Stock Profit/Loss Calculator",
    shortTitle: "Stock Profit/Loss",
    description:
      "Calculate profit/loss, percentage gain, and returns on stock investment",
    keywords: [
      "stock profit",
      "stock loss",
      "equity gain",
      "share return",
      "trading profit",
    ],
    inputs: [
      {
        type: "number",
        key: "quantity",
        label: "Number of Shares",
        suffix: "shares",
        min: 1,
        max: 1000000,
        step: 1,
        default: 100,
      },
      {
        type: "number",
        key: "buyPrice",
        label: "Buy Price per Share",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 500,
      },
      {
        type: "number",
        key: "sellPrice",
        label: "Sell Price per Share",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 650,
      },
      {
        type: "number",
        key: "brokerageBuy",
        label: "Brokerage on Buy",
        suffix: "%",
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.05,
      },
      {
        type: "number",
        key: "brokerageSell",
        label: "Brokerage on Sell",
        suffix: "%",
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.05,
      },
    ],
    calculate(v) {
      const qty = v.quantity as number;
      const buyPrice = v.buyPrice as number;
      const sellPrice = v.sellPrice as number;
      const brokeBuy = (v.brokerageBuy as number) / 100;
      const brokeSell = (v.brokerageSell as number) / 100;

      const buyAmount = qty * buyPrice;
      const buyBrokerage = buyAmount * brokeBuy;
      const totalCost = buyAmount + buyBrokerage;

      const sellAmount = qty * sellPrice;
      const sellBrokerage = sellAmount * brokeSell;
      const totalRevenue = sellAmount - sellBrokerage;

      const profit = totalRevenue - totalCost;
      const profitPercent = (profit / totalCost) * 100;

      return {
        totalCost: Math.round(totalCost),
        totalRevenue: Math.round(totalRevenue),
        profit: Math.round(profit),
        profitPercent: Math.round(profitPercent * 100) / 100,
        buyPrice: buyPrice,
        sellPrice: sellPrice,
      };
    },
    resultFields: [
      {
        key: "profit",
        label: "Net Profit/Loss",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "profitPercent",
        label: "Return %",
        format: "percent",
      },
      {
        key: "totalCost",
        label: "Total Cost (incl. brokerage)",
        format: "currency",
      },
      {
        key: "totalRevenue",
        label: "Net Proceeds",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Who Should Track Stock Profits",
            "body": "Active traders: need to track each trade for tax purpose. Long-term investors: monitor holdings for rebalancing. Retirees: harvesting gains gradually (tax planning). NRIs: tracking India holdings from abroad. Business owners: evaluating alternative income sources. Anyone with taxable capital gains. Portfolio managers: assessing performance metrics."
      },
      {
            "title": "Profit Calculation & Return Metrics",
            "body": "Profit = Selling Price - Cost Basis. Profit % = (Profit / Cost Basis) × 100. Annualized Return = (Selling Price / Cost Basis)^(1/years) - 1. Include all costs: purchase price, brokerage, taxes, fees. Final price = Selling Price - (brokerage + any selling charges). Net profit = Final Price - Cost Basis (including all costs). This calculator handles these computations precisely."
      },
      {
            "title": "Tax Planning: STCG vs LTCG Impact",
            "body": "STCG (<1 year): profit taxed as ordinary income at slab rate (10% slab = ₹10K tax on ₹1L profit; 30% slab = ₹30K). LTCG (>1 year): profit taxed at fixed 10%, but first ₹1L per FY is exempt (no tax). Strategy: hold winners >1 year to get LTCG benefit (save 10-20% tax). Harvest losses <1 year (tax-loss harvesting). Plan selling in financial year to optimize ₹1L LTCG exemption across multiple holdings."
      },
      {
            "title": "SEBI Rules on Stock Sale & Profit Reporting",
            "body": "SEBI regulates stock trading through exchanges. Profit reporting: mandatory in tax return (ITR-2 or ITR-3 for business). Form: Schedule CG (Capital Gains). Discrepancy in profit reporting triggers IT notice. TDS applicable on some gains. Accurate cost basis (via broker contract notes) essential. ITR reconciliation with exchange records required. All gains must be declared irrespective of amount."
      },
      {
            "title": "Real Example & Decision Framework",
            "body": "Bought SBI 100 shares at ₹550/share = ₹55K. Holding 8 months, current price ₹600. Profit = (600-550) × 100 = ₹5K = 9.09% absolute. STCG tax: if 20% slab, tax = ₹1K, net gain ₹4K. Hold 4 more months (>1 year): LTCG tax 10% = ₹500, net gain ₹4.5K. Decision: hold 4 months more to save ₹500. This calculator helps quantify tax impact of timing decisions."
      }
],
      
      whatIs:
        "This calculator computes your exact profit (or loss) from stock trading or investing. Buy ₹2 lakh of TCS stock, sell for ₹2.5 lakh—profit is ₹50K. But what's your return percentage? 25%? Over what time period? Return varies: 1 month = 300% annualized (unsustainable), 2 years = 11.18% annualized (reasonable). In India, profit classification matters for tax: Same-day or <1 year holding = STCG (Short-Term Capital Gain), taxed at your income slab rate (10-30%). >1 year holding = LTCG (Long-Term Capital Gain), taxed at 10% flat (with ₹1 lakh exemption per financial year). A ₹50K profit held 1.5 years pays only ₹5K tax (10%), but held 0.5 years pays ₹5-15K (depending on slab). This calculator helps compute profit/loss and understand annualized returns, critical for investment decisions and tax planning.",
      faqs: [
        {
          question:
            "Why is brokerage important in profit calculation?",
          answer:
            "Brokerage and transaction costs eat into profits. Example: 30% gain minus 0.1% brokerage = 29.8% net gain. Ignore it at your peril!",
        },
        {
          question: "Are there other costs besides brokerage?",
          answer:
            "Yes: transaction tax (0.1% in India), GST on brokerage (18%), exchange fees (minimal). Most brokers show these in your statement separately.",
        },
        {
          question: "How does this profit affect my tax liability?",
          answer:
            "Short-term (held <1 year): taxed as per your income tax slab. Long-term (held >1 year): 10% LTCG tax. Consult a CA for exact calculation.",
        },
        {
          question:
            "What if I bought and sold on the same day?",
          answer:
            "Intraday trading profit is short-term and taxed at your slab rate (up to 42.94%). Brokerage/STT still applies. Intraday is less tax-efficient.",
        },
        {
          question:
            "Should I sell or hold if profit is only ₹1,000?",
          answer:
            "Consider: Is the stock overvalued? If yes, sell (secure profit). If fundamentals are strong, hold (for long-term growth). Don't obsess over small gains.",
        },
      ],
      relatedSlugs: [
        "investment/stock-average-calculator",
        "investment/dividend-yield-calculator",
      ],
    },
  },

  // 14. Dividend Yield Calculator
  {
    slug: "dividend-yield-calculator",
    category: "investment",
    title: "Dividend Yield Calculator",
    shortTitle: "Dividend Yield",
    description:
      "Calculate annual dividend yield as a percentage of current stock price",
    keywords: [
      "dividend yield",
      "dividend income",
      "stock dividend",
      "passive income",
    ],
    inputs: [
      {
        type: "number",
        key: "annualDividend",
        label: "Annual Dividend per Share",
        prefix: "₹",
        min: 0,
        max: 1000,
        step: 0.1,
        default: 20,
      },
      {
        type: "number",
        key: "currentPrice",
        label: "Current Market Price",
        prefix: "₹",
        min: 1,
        max: 100000,
        step: 1,
        default: 500,
      },
      {
        type: "number",
        key: "shares",
        label: "Number of Shares Held",
        suffix: "shares",
        min: 1,
        max: 1000000,
        step: 1,
        default: 100,
      },
    ],
    calculate(v) {
      const annualDiv = v.annualDividend as number;
      const price = v.currentPrice as number;
      const shares = v.shares as number;

      const dividendYield = (annualDiv / price) * 100;
      const annualIncome = annualDiv * shares;

      return {
        dividendYield: Math.round(dividendYield * 100) / 100,
        annualIncome: Math.round(annualIncome),
        currentPrice: Math.round(price),
        shares: Math.round(shares),
      };
    },
    resultFields: [
      {
        key: "dividendYield",
        label: "Dividend Yield",
        format: "percent",
        isPrimary: true,
      },
      {
        key: "annualIncome",
        label: "Annual Dividend Income",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Dividend Yield vs Capital Appreciation",
            "body": "Dividend yield: regular cash income (5-8% typical). Capital appreciation: stock price growth (10-15% typical in bull markets). Trade-off: high dividend payers often grow slower (mature companies). Low dividend payers often grow faster (growth companies). Balanced approach: 60-70% in growth funds, 30-40% in dividend funds. Dividend yield suitable for: retirees (need cash flow), conservative investors (prefer income over growth), NRIs (wanting rupee repatriation)."
      },
      {
            "title": "Calculating True Dividend Yield",
            "body": "Formula: Dividend Yield = (Annual Dividend / Current Stock Price) × 100. If HDFC paying ₹150 annual dividend, stock at ₹2,500: yield = 6%. Track: dividend per share (DPS) over years. Trailing yield: based on last 12 months dividends (most relevant). Forward yield: based on expected next dividend. Payout ratio: dividend ÷ earnings (sustainable <60% is healthy). Growing dividend yield: dividend increases yearly (inflation protection)."
      },
      {
            "title": "Tax Treatment of Dividend Income in India",
            "body": "Current regime (2020+): dividends taxed as income at your slab rate (10-30%) plus 10% TDS. Example: 20% slab investor receiving ₹10K dividend pays ₹1K TDS + ₹1K additional = 20% total tax. Previous regime (till 2020): DDT (Dividend Distribution Tax) paid by company at flat 20.55%. New regime is better for low-income individuals (0-20% slab), worse for high-income (30% slab). Strategy: dividend funds suit 0-10% slab individuals best."
      },
      {
            "title": "SEBI Rules on Dividend Payout",
            "body": "Companies must follow Dividend Distribution Policy (DDP) under SEBI. Dividend Policy transparency required. Board approval required. Can't exceed 65% payout ratio (retain for growth). Listing norms require timely payout. Ex-dividend date crucial: own before ex-date to receive dividend. Dividend reinvestment option (DRIP) available in some schemes. Special dividends (one-time) can supplement regular dividends."
      },
      {
            "title": "Building a Dividend Portfolio",
            "body": "High-dividend stocks: HDFC Bank (5%), ICICI Bank (3-4%), Reliance (1-2%), Coal India (7-8%). Dividend mutual funds: HDFC Dividend Yield Fund, SBI Magnum Dividend Yield. Dividend ETFs: Nifty Dividend Opportunities 50 Index. Monthly dividend: PSU banks, FMCG companies. Quarterly dividend: most large-cap stocks. Combine dividend funds (30-40%) with growth funds (60-70%) for balanced returns + income. Review quarterly; exit if yield drops due to cutting dividend (distress signal)."
      }
],
      
      whatIs:
        "Dividend Yield measures the annual cash income you receive per rupee invested. A stock trading at ₹1,000 paying ₹50 annual dividend = 5% dividend yield. In India, dividends are paid by stable, mature companies (banks, telecom, FMCG, utilities) and popular indexes (Nifty 50, BSE Sensex). High dividend yield (6-8%) attracts income-focused investors, retirees, and NRIs seeking regular cash flow. However, high yield can signal distress (yield rises when stock price crashes, yield = dividend ÷ price). Dividend yield is taxed: until 2020, as dividend distribution tax (DDT) paid by company; now (2020+), dividend income in your hands taxed at slab rate + 10% TDS. This makes dividend-yield strategy less tax-efficient than capital appreciation (LTCG at 10%). This calculator helps evaluate dividend income potential, compare yields across stocks, and optimize dividend portfolio allocation. Essential for retirees, conservative investors, and NRI portfolio planners.",
      faqs: [
        {
          question:
            "What is a good dividend yield?",
          answer:
            "3-5% is decent for Indian blue-chip stocks. PSU banks/PSUs: 5-7%. This is higher than FD rates, but dividend growth uncertain. Compare against FD returns.",
        },
        {
          question:
            "Is dividend yield the same as dividend return?",
          answer:
            "No. Yield is annual dividend / current price. Total return = yield + capital appreciation/depreciation. A 4% yield stock can give -5% or +15% total return based on price movement.",
        },
        {
          question:
            "Why did dividend yield jump suddenly?",
          answer:
            "If dividend is same but stock price falls, yield increases (inverse relationship). Don't chase high yield from falling stocks; check if dividend is sustainable.",
        },
        {
          question:
            "Are dividends taxed?",
          answer:
            "Yes. Dividend Distribution Tax (DDT) is levied by mutual funds. For direct stocks, dividend received is taxed as income at your slab rate. Consult tax advisor.",
        },
        {
          question:
            "Should I invest only for dividend income?",
          answer:
            "No. Growth + dividend balance is ideal. Pure dividend chasers miss capital appreciation. Younger investors: prioritize growth. Retirees: balance growth/dividend.",
        },
      ],
      relatedSlugs: [
        "investment/stock-profit-calculator",
        "investment/stock-average-calculator",
      ],
    },
  },

  // 15. ELSS Calculator
  createSIPTool({
    slug: "elss-calculator",
    category: "investment",
    title: "ELSS Calculator - Tax Saving Investment",
    shortTitle: "ELSS SIP",
    description:
      "Calculate ELSS returns with 3-year tax lock-in and ₹1.5 lakh tax deduction",
    keywords: [
      "elss",
      "equity linked savings",
      "section 80c",
      "tax saving investment",
      "mutual fund",
    ],
    defaultMonthly: 10000,
    maxMonthly: 1000000,
    defaultRate: 12,
    defaultYears: 10,
    whatIs:
      "ELSS (Equity Linked Saving Scheme) is India's most tax-efficient investment vehicle for most individuals. Invest up to ₹1.5 lakh per financial year, deduct entire amount from taxable income (Section 80C of Income Tax Act), and pay zero tax on gains after 3 years. A 20% slab taxpayer investing ₹1.5 lakh saves ₹30K in tax immediately! Combined with long-term compounding, ELSS is powerful for wealth building. ELSS funds are equity mutual funds, so returns fluctuate (typical 12-15% CAGR historically), but the 3-year lock-in forces discipline and captures market cycles. Post lock-in, you can exit without exit load (others have 0.5-1% charges). In India's tax landscape, ELSS is preferred over traditional fixed-deposit tax-saving schemes (offering 4-6% returns), debt funds (taxed heavily), or NPS (contribution capped at ₹1.5-1.75 lakh). ELSS is especially powerful for young professionals building career-long wealth.",
    faqs: [
      {
        question:
          "How much tax can I save with ELSS?",
        answer:
          "Max ₹1.5 lakh investment per financial year gets full tax deduction. At 30% tax slab, you save ₹45,000 annually. If you invest ₹1.5L/year for 10 years in ELSS, cumulative savings = ₹4.5 lakh.",
      },
      {
        question:
          "Can I invest more than ₹1.5 lakh in ELSS?",
        answer:
          "Yes, but extra amount above ₹1.5 lakh (across all 80C investments) won't get tax deduction. Excess can still be invested for wealth creation, just no immediate tax benefit.",
      },
      {
        question:
          "Is 3-year lock-in compulsory?",
        answer:
          "Yes. You cannot withdraw before 3 years. After 3 years, fully open. But if invested long-term (5+ years), equity returns compound significantly.",
      },
      {
        question:
          "Should I choose ELSS over PPF or NSC?",
        answer:
          "ELSS > PPF/NSC for younger investors (long time horizon). Returns: ELSS 12%+, PPF 7.1%, NSC 7.7%. But ELSS has market risk; PPF is guaranteed. Balance both.",
      },
      {
        question:
          "Are ELSS dividends taxed?",
        answer:
          "ELSS gains after 1 year holding = 10% LTCG tax. Below 1 year = short-term, taxed as income. Dividend distribution from ELSS is also taxed. Always check your fund's dividend reinvestment option.",
      },
    ],
    relatedSlugs: [
      "investment/sip-calculator",
      "investment/ppf-calculator",
      "investment/nsc-calculator",
      "tax/section-80c-calculator",
    ],
  }),

  // 16. NPS Calculator
  {
    slug: "nps-calculator",
    category: "investment",
    title: "NPS Calculator - Retirement Corpus & Pension",
    shortTitle: "NPS Planning",
    description:
      "Calculate NPS corpus and estimated monthly pension at retirement",
    keywords: [
      "nps",
      "national pension scheme",
      "retirement corpus",
      "pension income",
      "retirement planning",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyContribution",
        label: "Monthly NPS Contribution",
        prefix: "₹",
        min: 500,
        max: 500000,
        step: 500,
        default: 10000,
      },
      {
        type: "number",
        key: "yearlyBonus",
        label: "Annual Bonus/Extra Investment",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 50000,
      },
      {
        type: "number",
        key: "currentAge",
        label: "Current Age",
        suffix: "years",
        min: 18,
        max: 65,
        step: 1,
        default: 35,
      },
      {
        type: "number",
        key: "retirementAge",
        label: "Retirement Age",
        suffix: "years",
        min: 18,
        max: 70,
        step: 1,
        default: 60,
      },
      {
        type: "select",
        key: "allocationScheme",
        label: "Asset Allocation",
        options: [
          { value: "aggressive", label: "Aggressive (80% E, 20% D)" },
          { value: "moderate", label: "Moderate (60% E, 40% D)" },
          { value: "conservative", label: "Conservative (40% E, 60% D)" },
        ],
        default: "moderate",
      },
    ],
    calculate(v) {
      const monthly = v.monthlyContribution as number;
      const yearly = v.yearlyBonus as number;
      const currentAge = v.currentAge as number;
      const retirementAge = v.retirementAge as number;
      const scheme = v.allocationScheme as string;

      let equityReturn = 0.10;
      let debtReturn = 0.07;

      if (scheme === "aggressive") {
        equityReturn = 0.11;
        debtReturn = 0.07;
      } else if (scheme === "moderate") {
        equityReturn = 0.10;
        debtReturn = 0.065;
      } else {
        equityReturn = 0.09;
        debtReturn = 0.065;
      }

      const yearsToRetirement = retirementAge - currentAge;

      // Simple future value calculation (monthly contributions + yearly bonus)
      const monthlyRate = equityReturn / 12;
      const months = yearsToRetirement * 12;

      let corpus = 0;
      for (let m = 0; m < months; m++) {
        if (m % 12 === 0 && m > 0) {
          corpus += yearly;
          corpus *= 1 + monthlyRate;
        }
        corpus = corpus * (1 + monthlyRate) + monthly;
      }

      // Estimated pension: 40% annuity at 6% return
      const annuityAmount = (corpus * 0.4) / 12 / 10;
      const monthlyPension = Math.round(
        (annuityAmount * 0.06) / 12 * 1000
      );

      return {
        corpus: Math.round(corpus),
        retirementAge: retirementAge,
        yearsToRetirement: yearsToRetirement,
        estimatedMonthlyPension: Math.round(monthlyPension),
        totalInvested: Math.round(monthly * months + yearly * yearsToRetirement),
      };
    },
    resultFields: [
      {
        key: "corpus",
        label: "Estimated NPS Corpus",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "estimatedMonthlyPension",
        label: "Estimated Monthly Pension (40% annuity)",
        format: "currency",
      },
      {
        key: "yearsToRetirement",
        label: "Years to Retirement",
        format: "number",
      },
    ],
    content: {
      sections: [
      {
            "title": "Who Benefits Most From ELSS",
            "body": "Salaried employees with stable income (>₹5 lakh p.a.). Young professionals (20-40 years) with long investment horizon. Self-employed/business owners. Anyone in 20%+ tax slab. Those lacking lumpsum capital (SIP in ELSS works). Perfect for: career wealth building (10-20 year tenure), tax optimization, growth targeting. Less suitable for: very conservative investors (equity risk), those requiring liquidity <3 years, or low-income individuals (no tax benefit if no tax liability)."
      },
      {
            "title": "Tax Savings Mechanics",
            "body": "Invest ₹1.5L in ELSS → deduct from total income. Result: reduced taxable income, lower tax slab. Example: 30% slab earner with ₹50L income: investing ₹1.5L makes taxable income ₹48.5L, saving ₹30K+ in tax. That ₹30K saved is free money, reinvestible for compounding. Over 20 years at 12% returns: ₹30K annually saved becomes ₹18-20 lakh corpus. ELSS tax saving = immediate 20-30% return + equity appreciation above that."
      },
      {
            "title": "SEBI Regulations & 3-Year Lock-In Rules",
            "body": "SEBI mandates 3-year lock-in from investment date. Cannot exit (redeem) before 3 years. Dividend reinvestment (if DRIP) also locked-in. After 3 years, fully redeemable anytime with zero load. Minimum investment: ₹500-1,000. No upper cap on amount but tax deduction capped at ₹1.5L p.a. under Section 80C. ELSS is 100% equity (no debt component), making it higher-risk but higher-return."
      },
      {
            "title": "ELSS Returns vs Other Tax-Saving Options",
            "body": "ELSS (12-15% CAGR typical): ₹1.5L invested annually becomes ₹3-4 crore in 25 years. Traditional savings scheme (4-6% CAGR): same ₹1.5L becomes ₹1-1.2 crore. NSC/KVP (6-8% CAGR): ₹1.5L becomes ₹1.5-2 crore. NPS (10-12% CAGR, Tier 1): ₹1.5L becomes ₹2.5-3.5 crore (but withdrawal restricted to 60 until 60). Winner: ELSS for growth + liquidity, NPS for retirement certainty."
      },
      {
            "title": "ELSS Investment Strategy & Fund Selection",
            "body": "Select diversified large-cap ELSS funds (lower volatility). Example: HDFC Tax Saver, Axis Long Term Equity, ICICI Pru Tax Plan. Check 3-year, 5-year CAGRs (should beat index). Expense ratio <1% preferred. Fund manager tenure >5 years (continuity). SIP approach: ₹12.5K monthly (₹1.5L annually) removes timing risk. Post-lock-in: stay invested or switch to liquid funds (don't exit). Rebalance yearly: if gains exceed 70% of portfolio, partially book profits into debt."
      }
],
      
      whatIs:
        "NPS (National Pension System) is India's modern retirement solution designed to replace the old civil service pension system and now open to all citizens. Unlike pensions (defined benefit), NPS is defined contribution—your final corpus depends on contributions, investment performance, and tenure. You contribute a percentage of salary (employee 10% + employer 10% typical), invest in equity/debt/money market funds per your choice, and at retirement (58-70 years), withdraw 60% as lumpsum and buy annuity with remaining 40%. In today's terms: ₹2-3 crore corpus by retirement is achievable for consistent 20+ year investors. The power of NPS: tax savings (₹1.5L deduction under 80C + additional ₹50K under 80CCD(1B) = ₹2L annually!), employer contribution (extra 20% to corpus), flexibility in fund choice, and relatively low expense ratios (0.01-0.35%). NPS is ideal for self-employed individuals (no employer contribution), freelancers, and anyone wanting tax-efficient retirement corpus. Especially powerful for young professionals starting at 25.",
      faqs: [
        {
          question:
            "What's the difference between NPS Tier-I and Tier-II?",
          answer:
            "Tier-I: 60-year lock-in, tax-deductible, restricted withdrawal. Tier-II: fully liquid (like mutual fund), no tax benefits. Most use Tier-I for retirement, Tier-II for emergency savings.",
        },
        {
          question:
            "Can I choose my asset allocation in NPS?",
          answer:
            "Yes, fully flexible. Options: Equity (E), Corporate bonds (C), Government securities (G), Liquid (L), Gold (G). You choose mix and rebalance annually. Or use auto-allocation portfolios.",
        },
        {
          question:
            "What happens at NPS maturity (retirement)?",
          answer:
            "You must buy an annuity for 40% of corpus (fixed pension for life). Remaining 60% you withdraw as lumpsum. Some flexibility in annuity options available.",
        },
        {
          question:
            "Is NPS better than mutual funds for retirement?",
          answer:
            "NPS pros: tax-deductible, lower fees (0.01-0.13%), guaranteed annuity. MF pros: flexible withdrawal, better tax efficiency post-1 year, higher potential returns. Use both: max NPS (tax benefit), rest in MF.",
        },
        {
          question:
            "Can I withdraw from NPS before retirement?",
          answer:
            "Tier-I: Limited. You can withdraw 50% at age 50 or for medical/higher education (only after 10 years). Tier-II: fully accessible. Plan for this restriction.",
        },
      ],
      relatedSlugs: [
        "retirement/nps-calculator",
        "investment/sip-calculator",
        "savings/ppf-calculator",
      ],
    },
  },

  // 17. PPF Calculator
  createCompoundInterestTool({
    slug: "ppf-calculator",
    category: "investment",
    title: "PPF Calculator - Public Provident Fund",
    shortTitle: "PPF Maturity",
    description:
      "Calculate PPF maturity value with ₹1.5L annual deduction benefit",
    keywords: [
      "ppf",
      "public provident fund",
      "retirement savings",
      "tax-free returns",
      "section 80c",
    ],
    defaultAmount: 150000,
    maxAmount: 1500000,
    defaultRate: 7.1,
    defaultYears: 15,
    maxYears: 50,
    compoundingFrequency: 1,
    whatIs:
      "PPF (Public Provident Fund) is India's most trusted, government-backed small savings scheme. Invest ₹500-1.5 lakh annually for 15 years, earn government-guaranteed 7-8% (currently 8.2%) p.a., and get complete tax exemption (EEE: Entry tax-free, Earning tax-free, Exit tax-free). At maturity (15 years), corpus is fully yours; extend for another 5, 10, 15 years earning same tax-free rate. A ₹1.5L annual investor for 15 years builds ₹40+ lakh corpus by age 60+. PPF is ideal for: conservative investors (zero risk—backed by government), salaried professionals (use as tax-save tool, duplicate of ELSS but lower returns), retirees (post-maturity extension for income), children's education fund (open account at birth, matures at 21). PPF carries sovereign guarantee—no default risk, making it psychologically reassuring for risk-averse Indians. Only drawback: lower returns (7-8%) vs equity (12-15%), but safety trade-off is worth it for portion of portfolio.",
    faqs: [
      {
        question:
          "Can I withdraw PPF before 15 years?",
        answer:
          "Partial withdrawal: After 7 years (up to 50% of balance). Full withdrawal: At 15 years. Or extend for further 5-year blocks. No withdrawal possible in first 6 years.",
      },
      {
        question:
          "Is PPF interest really tax-free?",
        answer:
          "Yes, completely. PPF contribution gets ₹1.5L deduction, interest earned is tax-free, and maturity amount is tax-free. Better than FD for tax savings.",
      },
      {
        question:
          "What if I miss a monthly contribution?",
        answer:
          "You can deposit any amount, any time in a financial year (max ₹1.5L annually). No penalty. If no deposit in a year, account becomes 'dormant' but earns returns. Pay ₹50 to reactivate.",
      },
      {
        question:
          "Should I maximize ₹1.5L each year?",
        answer:
          "Yes, if you have surplus. Tax benefit + guaranteed 7.1% return. No reason to not use this benefit. At 30% tax slab, you save ₹45K/year on tax.",
      },
      {
        question:
          "Can I use PPF for children's education fund?",
        answer:
          "Yes. PPF terms: max 50% withdrawal from 7th year. Perfect for 18-year education goals. Contribute ₹1.5L/year for 7 years, then withdraw 50% for college from year 8.",
      },
    ],
    relatedSlugs: [
      "savings/fd-calculator",
      "investment/elss-calculator",
      "savings/nsc-calculator",
      "tax/section-80c-calculator",
    ],
  }),

  // 18. Sukanya Samriddhi Yojana Calculator
  createCompoundInterestTool({
    slug: "ssy-calculator",
    category: "investment",
    title: "Sukanya Samriddhi Yojana Calculator",
    shortTitle: "SSY Returns",
    description:
      "Calculate SSY maturity value at 21 years with 8.2% guaranteed return",
    keywords: [
      "ssy",
      "sukanya samriddhi",
      "girl child",
      "education fund",
      "marriage fund",
    ],
    defaultAmount: 50000,
    maxAmount: 1500000,
    defaultRate: 8.2,
    defaultYears: 21,
    maxYears: 30,
    compoundingFrequency: 1,
    whatIs:
      "SSY (Sukanya Samriddhi Yojana) is India's flagship government scheme for girl child education and marriage funding. Open account for girl child <10 years, contribute ₹500-1.5 lakh annually for 14 years (or flex timing), earn government-guaranteed 8.2% p.a., and withdraw tax-free at age 18 (50% for education) and age 21 (100% for marriage). A parent investing ₹1 lakh annually for 14 years builds ₹20+ lakh corpus by age 21, all tax-free and guaranteed. SSY is ideal for: parents of girl children, grandparents (account opened on grandchild's name), guardians (legal guardians can open). It's as safe as PPF (government-backed), more generous (higher interest rate 8.2%), and purpose-specific (education + marriage). In India's context, SSY is a cultural-financial fit—combining government support for girls' education with savings discipline. SEBI-regulated, tax-guaranteed, no market risk.",
    faqs: [
      {
        question:
          "When can I withdraw from SSY?",
        answer:
          "You can withdraw 50% after age 18 (for education/marriage). Full withdrawal at age 21 (maturity). No partial withdrawal before 18.",
      },
      {
        question:
          "Can SSY maturity be extended?",
        answer:
          "Yes. After 21 years, you can extend for further 5-year blocks (up to age 26+). Extension earns same 8.2% rate. Useful if marriage is delayed.",
      },
      {
        question:
          "Is SSY better than FD or PPF?",
        answer:
          "SSY: 8.2% (highest), but locked 21 years. PPF: 7.1%, locked 15 years, more flexible. FD: 5-6%, fully liquid. For girl child savings: SSY > PPF > FD.",
      },
      {
        question:
          "Can parents of multiple daughters open multiple SSY accounts?",
        answer:
          "Yes, one account per girl child. Max ₹1.5L investment per girl per year across all accounts. If 2 daughters, invest ₹1.5L each (₹3L total).",
      },
      {
        question:
          "Is SSY fully tax-free?",
        answer:
          "Yes. Contribution: ₹1.5L deductible under Section 80C. Interest: completely tax-free. Maturity amount: tax-free. Best tax-efficient savings for girls.",
      },
    ],
    relatedSlugs: [
      "savings/ppf-calculator",
      "savings/fd-calculator",
      "investment/elss-calculator",
    ],
  }),

  // 19. KVP Calculator
  {
    slug: "kvp-calculator",
    category: "investment",
    title: "Kisan Vikas Patra Calculator",
    shortTitle: "KVP Doubling Time",
    description:
      "Calculate time to double your money in KVP at 7.5% return",
    keywords: [
      "kvp",
      "kisan vikas patra",
      "farmers scheme",
      "doubling returns",
      "rural savings",
    ],
    inputs: [
      {
        type: "number",
        key: "investment",
        label: "Investment Amount",
        prefix: "₹",
        min: 1000,
        max: 10000000,
        step: 1000,
        default: 100000,
      },
    ],
    calculate(v) {
      const amount = v.investment as number;
      const rate = 0.075;

      // Time to double: ln(2) / ln(1 + r)
      const timeToDouble = Math.log(2) / Math.log(1 + rate);
      const yearsToDouble = Math.round(timeToDouble * 10) / 10;
      const monthsToDouble = Math.round((timeToDouble % 1) * 12);

      const maturityAmount = amount * 2;

      return {
        investment: amount,
        maturityAmount: Math.round(maturityAmount),
        yearsToDouble: yearsToDouble,
        monthsToDouble: monthsToDouble,
        kvpReturnRate: 7.5,
      };
    },
    resultFields: [
      {
        key: "yearsToDouble",
        label: "Time to Double Your Money",
        format: "years",
        isPrimary: true,
      },
      {
        key: "monthsToDouble",
        label: "Additional Months",
        format: "number",
      },
      {
        key: "maturityAmount",
        label: "Maturity Amount (2x)",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Who Should Invest in PPF",
            "body": "Conservative investors (risk-averse). Retirees (safe income planning). Parents funding children education (open account for kid, matures when child is adult). Salaried employees (using ₹1.5L Section 80C limit). Self-employed (tax-saving without market risk). NRIs earning rupees (no restrictions on returns, just account opening). Anyone wanting zero-volatility, government-guaranteed returns."
      },
      {
            "title": "PPF Calculation & Compounding",
            "body": "Formula: FV = Annual Investment × [((1+r)^n - 1) / r] × (1+r). Where r = rate (7-8%), n = years. Example: ₹1.5L p.a. for 15 years at 8%: FV ≈ ₹40.2 lakh. Post-maturity extension (5 years): ₹40.2L becomes ₹59.5L. Further extension (5 years): ₹59.5L becomes ₹87.5L. This calculator handles all compounding automatically, showing year-by-year accumulation."
      },
      {
            "title": "Government Guarantee & Regulatory Framework",
            "body": "Backed by: Government of India (sovereign guarantee). Regulated by: Department of Post Office, Ministry of Communications. Interest rate: set by government, revised quarterly. Currently 8.2% (highest in 10+ years). No default risk whatsoever. Tax status: Exempt-Exempt-Exempt (EEE) under Income Tax Act. Maturity coverage: full corpus guaranteed, no caps or restrictions."
      },
      {
            "title": "PPF Flexibility: Withdrawal & Extension Options",
            "body": "Lock-in (first 7 years): no withdrawal (except age 18+, education loan). Partial withdrawal (year 8-14): 50% of balance or previous year balance, whichever less. Full maturity (year 15): withdraw entire amount or extend. Extension with withdrawals (post-maturity): withdraw interest only or interest + principal, both options available. Flexibility post-maturity unmatched among government schemes."
      },
      {
            "title": "Strategic Allocation: PPF + Equities Hybrid",
            "body": "Conservative portfolio: 50% PPF (₹75K p.a.) + 50% ELSS/mutual funds (₹75K p.a.). This diversifies between safety and growth. PPF provides stability, equity provides inflation-beating returns. Combined CAGR: 10-11% (midpoint between 8% PPF and 12-15% equity). Over 25 years: much higher corpus than PPF alone, with reasonable safety. Rebalance annually: ensure PPF ratio matches risk tolerance."
      }
],
      
      whatIs:
        "Kisan Vikas Patra (KVP) is India's small savings scheme doubling your money in 115 months (~9.6 years) at 8.1% p.a. (highest guaranteed rate among post office schemes). An investment of ₹10 lakh becomes ₹20 lakh in 9.6 years, tax-free! KVP is specifically designed for farmers and rural investors needing capital accumulation, but open to all Indians. Invest ₹1,000 minimum, maximum ₹10 lakh per person, per financial year. Interest is compounded, adding to principal automatically. KVP is safer than equity (no market risk) but offers better returns than FDs (8% FD vs 8.1% KVP, similar but KVP allows flexible maturity). Ideal for: risk-averse investors, farmers needing capital for expansion, parents building education fund for kids, retirees seeking safe growth. Government-backed (sovereign guarantee), tax-free growth, no compliance hassle.",
      faqs: [
        {
          question:
            "How long does KVP take to double?",
          answer:
            "At 7.5% returns, KVP doubles your money in ~9.5 years (124 months). E.g., ₹1L becomes ₹2L in 9.5 years.",
        },
        {
          question:
            "Can I get money before doubling?",
          answer:
            "Yes. You can withdraw anytime (but no lock-in officially). Interest is credited half-yearly. If withdrawn early, you get pro-rata interest, not doubling benefit.",
        },
        {
          question:
            "Is KVP taxed?",
          answer:
            "KVP interest is taxed as income (not capital gains). If you earn ₹50K interest, it's taxable. Invest through Sukanya's parents' account, it becomes tax-efficient.",
        },
        {
          question:
            "Where can I buy KVP?",
          answer:
            "Post offices primarily. Some banks (SBI, Bank of India) also sell. Minimum ₹1,000, maximum ₹9.99 lakh per financial year per individual.",
        },
        {
          question:
            "Is KVP better than PPF?",
          answer:
            "KVP: 7.5%, doubling in ~9.5 years. PPF: 7.1%, must hold 15 years for 2x. KVP is faster, but PPF is more flexible (partial withdrawal from 7yr) and tax-deductible.",
        },
      ],
      relatedSlugs: [
        "savings/ppf-calculator",
        "savings/fd-calculator",
        "savings/nsc-calculator",
      ],
    },
  },

  // 20. NSC Calculator
  createCompoundInterestTool({
    slug: "nsc-calculator",
    category: "investment",
    title: "NSC Calculator - National Savings Certificate",
    shortTitle: "NSC Maturity",
    description: "Calculate National Savings Certificate maturity and break-even period",
    keywords: [
      "nsc",
      "national savings certificate",
      "post office savings",
      "guaranteed returns",
      "safe investment",
    ],
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 7.7,
    defaultYears: 5,
    maxYears: 20,
    compoundingFrequency: 1,
    whatIs:
      "NSC (National Savings Certificate) is India's most popular post office savings scheme, offering government-guaranteed 8.1% p.a. interest with complete tax exemption. Invest ₹100-minimum (no upper cap), compounds automatically for 5 years, and mature amount is completely tax-free. A ₹1 lakh NSC invested for 5 years becomes ₹1.47 lakh—all gains are tax-free and guaranteed. NSC is India's trusted middle-class savings tool due to zero risk (government backing), regular interest crediting (annually or monthly), and tax-free growth. Ideal for: conservative savers, retirees, salaried professionals seeking safe corpus, parents building children's education fund. NSC can be pledged for loans (LIC loans, bank loans accept NSC as collateral). Transfer allowed to beneficiaries (inheritance planning). Biggest advantage: automatic renewal—matured NSC can renew within 2 years without re-investment hassle.",
    faqs: [
      {
        question:
          "Can I break NSC before 5 years?",
        answer:
          "Partial break allowed after 1 year (minimal penalty). Full break after 2 years (interest penalty of ~5%). Not recommended; hold full 5 years for best return.",
      },
      {
        question:
          "How does NSC compare to fixed deposits?",
        answer:
          "NSC: 7.7% guaranteed, tax-deductible (₹1L/year), held at post office. FD: 5-6%, not tax-deductible (unless tax-saver), held at banks. NSC wins for tax efficiency.",
      },
      {
        question:
          "Can I reinvest NSC maturity?",
        answer:
          "Yes. At maturity, you can immediately open new NSC for continued growth. Or withdraw if you need funds. Reinvestment extends your lock-in but compounds growth.",
      },
      {
        question:
          "Is NSC interest taxed?",
        answer:
          "NSC interest is taxable as income. But ₹1L contribution gets Section 80C deduction, offsetting some tax. Effective tax on 7.7% return: ~2-3% for mid-bracket earner.",
      },
      {
        question: "Can seniors invest in NSC?",
        answer:
          "Yes, any age. No special senior scheme for NSC (unlike Senior Citizen Savings Scheme). Returns same 7.7% for all.",
      },
    ],
    relatedSlugs: [
      "savings/fd-calculator",
      "savings/ppf-calculator",
      "savings/kvp-maturity",
      "tax/section-80c-calculator",
    ],
  }),

  // 21. Post Office MIS
  {
    slug: "post-office-mis",
    category: "investment",
    title: "Post Office Monthly Income Scheme Calculator",
    shortTitle: "Post Office MIS",
    description:
      "Calculate monthly income from Post Office MIS investment (5 years)",
    keywords: [
      "post office mis",
      "monthly income",
      "fixed income",
      "senior citizens",
      "retirement income",
    ],
    inputs: [
      {
        type: "number",
        key: "investmentAmount",
        label: "Investment Amount",
        prefix: "₹",
        min: 1500,
        max: 45000000,
        step: 500,
        default: 500000,
      },
      {
        type: "number",
        key: "years",
        label: "Investment Period",
        suffix: "years",
        min: 1,
        max: 10,
        step: 1,
        default: 5,
      },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const years = v.years as number;
      const annualRate = 0.071; // 7.1% current rate

      const monthlyRate = annualRate / 12;
      const months = years * 12;

      const maturityAmount =
        amount *
        Math.pow(1 + monthlyRate, months);

      const monthlyIncome = Math.round((maturityAmount - amount) / months);

      return {
        investmentAmount: amount,
        maturityAmount: Math.round(maturityAmount),
        totalIncome: Math.round(maturityAmount - amount),
        monthlyIncome: monthlyIncome,
        years: years,
      };
    },
    resultFields: [
      {
        key: "monthlyIncome",
        label: "Estimated Monthly Income",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "maturityAmount",
        label: "Maturity Amount",
        format: "currency",
      },
      {
        key: "totalIncome",
        label: "Total Interest Earned",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "NSC Features & Who Should Invest",
            "body": "Conservative investors (zero market risk). Salaried employees (safe corpus building). Retirees (inflation-protected income). Parents (children education savings). Individuals tax-averse (complete tax exemption). Those wanting government backing (sovereign guarantee). Anyone >18 years old eligible (minors via guardians)."
      },
      {
            "title": "NSC Compounding & Maturity Calculation",
            "body": "Interest rate: 8.1% p.a. (government-set, locked). Compounding: annual, calculated on principal+accumulated interest. Formula: Maturity Amount = P × (1 + r)^5, where P = principal, r = 8.1%. Example: ₹1L × 1.081^5 = ₹1.47 lakh. Interest credited annually to principal (auto-compounding). Partial withdrawal allowed after 1 year (₹100 or interest, whichever less). Full withdrawal from maturity onwards."
      },
      {
            "title": "SEBI & Government Regulations",
            "body": "Regulated by: Department of Posts, Ministry of Communications. Backed by: Government of India (sovereign guarantee, full safety). Interest rate: locked for 5-year tenure (no rate risk). Deductibility: contribution NOT deductible under any tax section. Maturity amount: completely tax-free (no income tax, no capital gains tax). Legal status: statutory instrument under National Small Savings Scheme."
      },
      {
            "title": "Tax-Free Growth & Withdrawal Benefits",
            "body": "Complete exemption: interest earned is fully tax-free (no TDS, no return filing). Maturity withdrawal: tax-free amount received. Premature withdrawal (after 1 year, before 5 years): allowed, interest reduced (penalty applies). Post-maturity withdrawal: anytime, fully tax-free. Renewal: auto-renew within 2 years of maturity (convenience benefit)."
      },
      {
            "title": "NSC Investment Ladder Strategy",
            "body": "Recommended approach: invest ₹2-5L annually in separate NSC batches maturing at different years (staggered maturity). Benefit: guaranteed liquidity stream (each batch matures sequentially), prevents need to break prematurely. Example: NSC1 (matures year 5), NSC2 (matures year 6), NSC3 (matures year 7). Monthly liquidity: reinvest matured amount + new savings in new NSC batch. Ideal for retirees needing regular cash flow without selling at loss."
      }
],
      
      whatIs:
        "Post Office MIS (Monthly Income Scheme) is a government-backed savings scheme designed for regular income seekers. Invest a lumpsum (₹1,000-minimum), earn guaranteed 8.6% p.a., receive fixed monthly income (₹1,000 per ₹10,000 invested, roughly 10% p.a. as monthly installments), and recover principal at maturity (5 years). If you invest ₹10 lakh, receive ₹8,600 monthly income (8.6%) for 5 years, then recover ₹10 lakh entirely. MIS is ideal for: retirees (creates fixed monthly income), income-focused investors, parents funding their child's regular expenses. Unlike annuities (irreversible), MIS allows principal withdrawal at maturity. Tax: monthly income is taxable (at slab rate, TDS applicable), but principal recovery is tax-free. In India's low-interest environment (FD rates 6-7%), MIS at 8.6% is attractive for income seekers willing to lock capital 5 years.",
      faqs: [
        {
          question:
            "Is Post Office MIS income taxed?",
          answer:
            "Yes, monthly income is taxed as per your income slab. For retired persons, if income is below ₹5L, tax may be minimal. Consult tax advisor.",
        },
        {
          question:
            "What happens after 5 years?",
          answer:
            "Maturity amount is credited to your bank account. You can reinvest in new MIS, or use for other investments. No compulsory reinvestment.",
        },
        {
          question:
            "Can I break MIS before 5 years?",
          answer:
            "Yes, but with penalty: no interest if break within 1 year, 1% interest penalty if 1-3 years, 0.5% penalty if 3+ years. Avoid early break.",
        },
        {
          question:
            "Is Post Office MIS better than bank FD?",
          answer:
            "Post Office MIS: 7.1%, monthly income, government-backed. Bank FD: 5-6%, income on maturity, FDIC-insured. MIS better for monthly income; FD better for liquidity.",
        },
        {
          question:
            "Can NRIs invest in Post Office MIS?",
          answer:
            "No. Post Office MIS is for Indian residents only. NRIs must use NRO accounts for post office schemes.",
        },
      ],
      relatedSlugs: [
        "savings/fd-calculator",
        "savings/ppf-calculator",
        "savings/scss-returns",
      ],
    },
  },

  // 22. SCSS Calculator (Senior Citizens Savings Scheme)
  {
    slug: "scss-calculator",
    category: "investment",
    title: "SCSS Calculator - Senior Citizens Savings",
    shortTitle: "SCSS Returns",
    description:
      "Calculate SCSS maturity with 8.2% guaranteed return for seniors",
    keywords: [
      "scss",
      "senior citizens savings scheme",
      "retirement income",
      "elderly savings",
      "post office scheme",
    ],
    inputs: [
      {
        type: "number",
        key: "investment",
        label: "Investment Amount",
        prefix: "₹",
        min: 1000,
        max: 30000000,
        step: 1000,
        default: 500000,
      },
      {
        type: "number",
        key: "investmentYears",
        label: "Time Period",
        suffix: "years",
        min: 1,
        max: 10,
        step: 1,
        default: 5,
      },
    ],
    calculate(v) {
      const amount = v.investment as number;
      const years = v.investmentYears as number;
      const rate = 0.082;

      const maturityAmount = amount * Math.pow(1 + rate, years);
      const totalInterest = maturityAmount - amount;

      // SCSS is paid quarterly
      const quarterlyRate = rate / 4;
      const quarters = years * 4;
      const maturityQuarterly =
        amount * Math.pow(1 + quarterlyRate, quarters);

      return {
        investment: amount,
        maturityAmount: Math.round(maturityQuarterly),
        totalInterest: Math.round(maturityQuarterly - amount),
        annualIncome: Math.round((maturityQuarterly - amount) / years),
        quarterlyIncome: Math.round(
          ((maturityQuarterly - amount) / years) / 4
        ),
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalInterest",
        label: "Total Interest Earned",
        format: "currency",
      },
      {
        key: "quarterlyIncome",
        label: "Quarterly Interest Income",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Eligibility & Who Benefits Most",
            "body": "Age 60+ (any Indian citizen). Age 55+ (retired bank or government employee). Age 50+ (JCO/OR retiring from armed forces). Single person or joint account (couples can open separate accounts). Can open multiple accounts if surplus funds. Benefits: safe income stream (no market risk), quarterly payout (cash flow planning), principal preservation (full recovery at maturity)."
      },
      {
            "title": "SCSS Income Calculation & Structure",
            "body": "Investment: ₹1,000-30 lakh per person. Interest rate: 8.2% p.a. (highest for senior citizen schemes). Payout: quarterly (every 3 months). Quarterly income = Annual interest ÷ 4. Example: ₹30L investment → ₹2.46L annual income → ₹61,500 quarterly. After 5 years: ₹30L principal returned fully. Total cash received: ₹30L (principal) + ₹12.3L (interest) = ₹42.3L."
      },
      {
            "title": "SCSS Regulatory Framework & Safety",
            "body": "Regulated by: Department of Posts, Ministry of Communications. Backed by: Government of India (sovereign guarantee). Interest rate: government-set, 8.2% locked. Account opened at post office, banks, or online. Maturity: exactly 5 years, renew for another 5 years at new rates (rate reset per scheme cycle). Principal: fully guaranteed, no market risk whatsoever."
      },
      {
            "title": "Tax Treatment & Quarterly Income Planning",
            "body": "Quarterly income: taxable as interest income at slab rate (10-30%). TDS: 10% deducted if annual income >₹10,000 (unless >₹5L total income, can file Form 15G). Principal withdrawal: completely tax-free. Net return: after 20% average tax, effective return ≈ 6.6% p.a. Still higher than FD. Strategy: open SCSS accounts in both spouses' names to split income and reduce tax bracket."
      },
      {
            "title": "SCSS Retirement Planning & Portfolio Allocation",
            "body": "Recommended portfolio (for ₹50L capital): SCSS ₹30L (₹61.5K quarterly income) + FD ₹15L (₹90K annual income) + Liquid Fund ₹5L (emergency). Total monthly income: ₹20.5K from SCSS, ₹7.5K from FD = ₹28K monthly (₹3.36L annually). Diversification: protects from scheme changes. Renewal: SCSS can renew after 5 years at new government-set rates. Combine with NPS pension for layered income security."
      }
],
      
      whatIs:
        "SCSS (Senior Citizen Savings Scheme) is India's flagship retirement savings scheme exclusively for senior citizens (60+ years, or 55+ retired bank/government employees). Deposit ₹1,000-minimum (max ₹30 lakh), earn 8.2% p.a. government-guaranteed interest, and receive quarterly income. Unique feature: principal is returned entirely at maturity (5 years). A retiree investing ₹30 lakh receives ₹61,500 quarterly income (₹2.46L annually) for 5 years, then entire ₹30 lakh back. SCSS is ideal for: newly retired professionals (55-70 years), widows/widowers seeking safe income, anyone wanting higher returns than FDs. Tax: quarterly income is taxable (TDS at 10% for income >₹10K p.a.). SCSS is less popular than it should be—many seniors don't know about it. It provides better returns than FD (8.2% vs 6-7%), and income stream crucial for retired lifespan (20-30+ years post-retirement).",
      faqs: [
        {
          question:
            "Who is eligible for SCSS?",
          answer:
            "Indian citizens aged 60+ years. Retired government employees from age 55+ (with proof of retirement). Joint account with spouse (both must be 60+).",
        },
        {
          question:
            "Is SCSS interest taxed?",
          answer:
            "Yes. Interest is taxable as income. For seniors below taxable limit (~₹5L income), tax may be nil. Check with tax advisor; Form 60 may help reduce TDS.",
        },
        {
          question:
            "Can I break SCSS before maturity?",
          answer:
            "Yes. Withdrawal after 1 year: ₹100 penalty + no interest for 3 months. After 2 years: 1% penalty on principal. Avoid early break if possible.",
        },
        {
          question:
            "Is SCSS better than FD for seniors?",
          answer:
            "SCSS: 8.2%, government-backed, highest rate for seniors. FD: 5-6%. Senior-specific FD (age 60+): 6-7%. SCSS is clearly better for safety + returns.",
        },
        {
          question:
            "Can I renew SCSS after maturity?",
          answer:
            "Yes. You can immediately reinvest maturity in new SCSS. New account starts fresh 5-year period at prevailing SCSS rate (usually good for seniors).",
        },
      ],
      relatedSlugs: [
        "savings/ppf-calculator",
        "savings/post-office-mis",
        "savings/fd-calculator",
      ],
    },
  },

  // 23. Real Return Calculator
  {
    slug: "real-return-calculator",
    category: "investment",
    title: "Real Return Calculator - Inflation-Adjusted Return",
    shortTitle: "Real Return",
    description:
      "Calculate inflation-adjusted returns to see true purchasing power growth",
    keywords: [
      "real return",
      "inflation adjustment",
      "purchasing power",
      "true return",
      "real value",
    ],
    inputs: [
      {
        type: "number",
        key: "nominalReturn",
        label: "Nominal Return",
        suffix: "%",
        min: -100,
        max: 100,
        step: 0.5,
        default: 12,
      },
      {
        type: "number",
        key: "inflationRate",
        label: "Inflation Rate",
        suffix: "%",
        min: 0,
        max: 30,
        step: 0.1,
        default: 5,
      },
    ],
    calculate(v) {
      const nominal = (v.nominalReturn as number) / 100;
      const inflation = (v.inflationRate as number) / 100;

      // Real return = (1 + nominal) / (1 + inflation) - 1
      const realReturn =
        (1 + nominal) / (1 + inflation) - 1;

      return {
        nominalReturn: Math.round((v.nominalReturn as number) * 100) / 100,
        inflationRate: v.inflationRate as number,
        realReturn: Math.round(realReturn * 10000) / 100,
        purchasingPowerRetain: Math.round((1 / (1 + inflation)) * 10000) / 100,
      };
    },
    resultFields: [
      {
        key: "realReturn",
        label: "Real Return (Inflation-Adjusted)",
        format: "percent",
        isPrimary: true,
      },
      {
        key: "nominalReturn",
        label: "Nominal Return",
        format: "percent",
      },
      {
        key: "inflationRate",
        label: "Inflation Rate",
        format: "percent",
      },
    ],
    content: {
      sections: [
      {
            "title": "Understanding Nominal vs Real Returns",
            "body": "Nominal return: raw investment gain (FD 8% = ₹8K on ₹1L). Real return: gain after inflation adjustment. If inflation 5%, real return = 8% - 5% = 3% (only ₹3K real wealth gain). Formula: Real Return = (1 + Nominal) / (1 + Inflation) - 1. Over decades, compounding difference is massive. ₹1L at 8% nominal for 25 years becomes ₹6.85 crore nominal but ₹1.59 crore in today's purchasing power (at 5% inflation)."
      },
      {
            "title": "India's Inflation Context & Impact",
            "body": "Historical inflation: 5-6% p.a. average (₹100 today = ₹250-300 in 20 years). Savings account (2-3% nominal): negative real return (-2 to -4% annually = purchasing power loss). FD (6-7% nominal): minimal real return (0-2%, barely beating inflation). Equity (12-15% nominal): strong real return (6-9%, true wealth builder). Government bonds (6-8% nominal): modest real return (0-3%). Strategy: use savings account only for emergency funds (liquidity matters, returns don't). Invest rest in equities for real wealth growth."
      },
      {
            "title": "SEBI & Tax Considerations in Real Returns",
            "body": "Real returns calculation ignores taxes (reported as pre-tax). Most important: calculate real returns post-tax. Example: FD 7%, tax 30% slab = 4.9% post-tax nominal. Inflation 5% = -0.1% real return (you're losing money!). Equity LTCG 10%: after 10% tax = 9% post-tax nominal, minus 5% inflation = 4% real return. Tax-free instruments (PPF 8%, no tax): 8% nominal - 5% inflation = 3% real return (excellent). This shows why tax-free instruments beat tax-heavy FDs in real terms."
      },
      {
            "title": "Real Return Impact Over Investment Lifetime",
            "body": "Example scenario: ₹50L capital, 25-year timeline, 5% inflation. Savings account (2% nominal, 0% real after inflation): becomes ₹63.8L nominal = ₹24.9L real (61% real loss!). FD (6% nominal, 0.95% real): becomes ₹215L nominal = ₹84L real (33% loss). Equity (12% nominal, 6.67% real): becomes ₹4.46 crore nominal = ₹1.74 crore real (248% real gain). Difference: ₹1.74 crore vs ₹24.9L = 70x more wealth through real return focus! This is wealth-building power of beating inflation through equity investing."
      },
      {
            "title": "Action Steps for Real Return Optimization",
            "body": "Analyze portfolio: calculate real returns (post-tax, post-inflation) for every holding. Shift from FD/savings accounts to equity/tax-free schemes (PPF, ELSS, NPS). Allocate: 50%+ to equity (long-term real growth), 30% to debt/balanced (stability), 20% to cash (liquidity). Monitor annually: compare real return vs target (3-4% real return minimum). Increase with inflation: raise investment amounts annually 5-6% to maintain purchasing power of future corpus. Compound real returns: power of real return compounding over 25-30 years is life-changing (₹1L → ₹4-5L in real terms)."
      }
],
      
      whatIs:
        "Nominal return is what you earn; real return is what you actually gain after inflation. Earn 8% in FD, but inflation is 6%? Real return is only 2% (that's true purchasing power gain). This is crucial in India where inflation consistently runs 5-6% p.a. A ₹1 crore fund that grows nominally at 8% for 20 years becomes ₹4.66 crore nominally, but in today's rupee value, it's only ₹2.16 crore (real). The inflation-adjusted calculator reveals whether your investments truly beat inflation. Most retail investors ignore real returns, leading to illusion of wealth growth while purchasing power actually declines. In India's moderate inflation (5-6%), equity (12-15% nominal) provides real returns of 6-9% (excellent), debt (5-7% nominal) provides 0-2% real returns (poor), and savings accounts (2-3% nominal) provide negative real returns. This calculator motivates shifting from safe but inflation-eroding fixed deposits to growth investments needed to preserve and grow wealth in real terms.",
      faqs: [
        {
          question:
            "Why is real return important?",
          answer:
            "Nominal return misleads. ₹1L earning 6% interest becomes ₹1.06L nominally, but costs more due to inflation. Real return shows if you're actually getting richer.",
        },
        {
          question:
            "What is India's average inflation rate?",
          answer:
            "Historically 5-6% (long-term average). Recent years: 4-7%. Use 5-6% for realistic planning. RBI targets 4%.",
        },
        {
          question:
            "If real return is negative, should I avoid that investment?",
          answer:
            "Depends on your need. Negative real return from FD vs inflation isn't great, but better than no investment. Use FD for safety (emergency fund), not wealth growth.",
        },
        {
          question:
            "How does real return affect retirement planning?",
          answer:
            "Critical! If your corpus grows at 8% nominal but inflation is 5%, real growth = 2.86%. Plan retirement corpus assuming 3-4% real return, not 8%.",
        },
        {
          question:
            "Which investments have high real returns?",
          answer:
            "Equity (12% nominal, 6-7% real). Real estate (8-10% nominal, 3-5% real). Gold (5% nominal, flat/negative real). Bonds (6% nominal, 0-1% real).",
        },
      ],
      relatedSlugs: [
        "investment/cagr-calculator",
        "investment/inflation-adjusted-return",
        "savings/fd-calculator",
      ],
    },
  },

  // 24. Inflation-Adjusted Return
  {
    slug: "inflation-adjusted-return",
    category: "investment",
    title: "Inflation-Adjusted Returns Calculator",
    shortTitle: "Inflation-Adjusted Return",
    description:
      "Calculate what your investment returns are worth after inflation",
    keywords: [
      "inflation adjusted",
      "after inflation",
      "real value",
      "purchasing power",
      "inflation impact",
    ],
    inputs: [
      {
        type: "number",
        key: "investmentAmount",
        label: "Original Investment",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 1000,
        default: 500000,
      },
      {
        type: "number",
        key: "returns",
        label: "Absolute Returns Earned",
        prefix: "₹",
        min: 0,
        max: 100000000,
        step: 1000,
        default: 200000,
      },
      {
        type: "number",
        key: "years",
        label: "Time Period",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 5,
      },
      {
        type: "number",
        key: "inflation",
        label: "Annual Inflation Rate",
        suffix: "%",
        min: 0,
        max: 20,
        step: 0.5,
        default: 5,
      },
    ],
    calculate(v) {
      const investment = v.investmentAmount as number;
      const returns = v.returns as number;
      const years = v.years as number;
      const inflation = (v.inflation as number) / 100;

      const totalAmount = investment + returns;
      const inflationFactor = Math.pow(1 + inflation, years);
      const realValue = totalAmount / inflationFactor;
      const realGain = realValue - investment;
      const realGainPercent = (realGain / investment) * 100;

      return {
        nominalAmount: Math.round(totalAmount),
        realValue: Math.round(realValue),
        realGain: Math.round(realGain),
        realGainPercent:
          Math.round(realGainPercent * 100) / 100,
        inflationEroded: Math.round(totalAmount - realValue),
      };
    },
    resultFields: [
      {
        key: "realValue",
        label: "Real Value (Inflation-Adjusted)",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "nominalAmount",
        label: "Nominal Value (Before Inflation)",
        format: "currency",
      },
      {
        key: "inflationEroded",
        label: "Wealth Lost to Inflation",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "Why Inflation-Adjusted Values Matter",
            "body": "Nominal value: face amount (what calculator shows). Real value: purchasing power (what you can actually buy). Example: your target is ₹1 crore corpus in 20 years. If inflation 5%, you actually need ₹2.65 crore nominally (to have ₹1 crore today's purchasing power). This calculator shows both. Understanding real needs prevents under-saving—many assume ₹1 crore is enough for retirement without inflation adjustment, leading to insufficient corpus."
      },
      {
            "title": "Inflation in India: Historical & Projected",
            "body": "Historical inflation: 5-6% average p.a. Food inflation: 6-8% (higher than general). Education inflation: 7-9% (crucial for parents). Healthcare inflation: 6-8%. Real estate inflation: 8-10%. Using conservative 5% assumption is reasonable for long-term planning. Some years spike 7-8%, some are 3-4%, so 5-6% average is middle ground. Planning assumption: never assume <5% inflation for India (historically underfulfilled)."
      },
      {
            "title": "Real vs Nominal Goal Setting",
            "body": "Nominal goal: \"₹1 crore by 60\" (doesn't account for inflation). Real goal: \"₹1 crore in today's value by 60 = ₹2.65 crore in 20 years' rupees.\" Big difference in savings required! SIP for nominal ₹1 crore: ₹30K/month might suffice. SIP for real ₹1 crore (₹2.65 crore nominal): ₹75K/month needed. This calculator prevents under-planning mistakes by clearly showing both nominal and real corpus needed."
      },
      {
            "title": "Retirement Planning With Inflation",
            "body": "Current annual expense: ₹24L. Planned retirement: 20 years. Inflation: 5% p.a. First year retirement expense (20 years from now): ₹24L × 1.05^20 = ₹63.5L. Annual expenses rising each year (inflation-adjusted): year 2 = ₹66.7L, year 3 = ₹70L, etc. Total 20-year retirement expense: ₹1.7 crore (not ₹4.8 crore). This inflation-escalating expense model is realistic and helps size corpus correctly. Simple ₹24L × 20 = ₹4.8L model is wildly wrong."
      },
      {
            "title": "Investment Strategy Post-Inflation Awareness",
            "body": "Key insight: nominal returns looking good can still mean real loss. 7% FD return in 6% inflation = 0.95% real (terrible). Shift strategy: prioritize real returns (equity beating inflation by 6-9% = true wealth growth). Allocation: 60% equity (real return 6-9%), 30% debt (real return 0-2%), 10% cash (real return negative, but needed for liquidity). Increase investments: raise SIP amount 5-6% annually to maintain purchasing power preservation. Regular review: calculate real return quarterly to stay motivated on actual wealth growth (not nominal illusions)."
      }
],
      
      whatIs:
        "This calculator shows what your money will actually be worth in the future when adjusted for inflation. Invest ₹1 crore today at 10% returns for 20 years, you get ₹6.7 crore nominally. But if inflation averages 5% p.a., that ₹6.7 crore can only buy what ₹2.48 crore can buy today (73% real purchasing power loss!). Most investors ignore this harsh reality. They feel wealthy seeing nominal corpus grow, unaware inflation silently erodes value. In India, retirement planning is often done on nominal basis (\"I need ₹1 crore at 60\"), but realistic planning requires inflation-adjusted values (\"I need ₹3-4 crore at 60 in future rupees\"). This calculator forces realistic forward-looking: shows both nominal corpus and inflation-adjusted value at future dates, motivating higher savings rate and better asset allocation. Essential for long-term planning (education fund, retirement corpus, wealth targets) where inflation compounds significantly.",
      faqs: [
        {
          question:
            "How much of my returns are lost to inflation?",
          answer:
            "Depends on holding period and inflation. Over 10 years at 5% inflation, ~39% of nominal value is eroded. Higher inflation + longer period = more loss.",
        },
        {
          question:
            "Should I avoid low-return safe investments like FD?",
          answer:
            "Not entirely. FD preserves capital for emergency funds (safety first). But for growth corpus, invest in equity where real returns (6-7%) beat inflation significantly.",
        },
        {
          question:
            "What real gain should I target?",
          answer:
            "For retirement, target 3-4% real return (nominal 8-9%). For education fund, 5-6% real (nominal 10-11%). These account for inflation while building wealth.",
        },
        {
          question:
            "Is inflation same for all product categories?",
          answer:
            "No. Food inflation: 5-7%, Medical: 8-10%, Education: 10-12%, Housing: 5-8%. Plan accordingly; education may need higher returns.",
        },
        {
          question:
            "How do I protect my wealth from inflation?",
          answer:
            "Invest in assets that beat inflation: Equity, Real Estate, Gold (inflation hedge). Avoid: Cash, FD alone (real return negative). Diversify across all three.",
        },
      ],
      relatedSlugs: [
        "investment/real-return-calculator",
        "investment/cagr-calculator",
        "savings/fd-calculator",
      ],
    },
  },

  // 25. Portfolio Rebalancing
  {
    slug: "portfolio-rebalancing",
    category: "investment",
    title: "Portfolio Rebalancing Calculator",
    shortTitle: "Portfolio Rebalancing",
    description:
      "Calculate rebalancing amounts to restore target asset allocation",
    keywords: [
      "portfolio rebalancing",
      "asset allocation",
      "target allocation",
      "rebalancing strategy",
      "portfolio management",
    ],
    inputs: [
      {
        type: "number",
        key: "equityTarget",
        label: "Target Equity Allocation",
        suffix: "%",
        min: 0,
        max: 100,
        step: 5,
        default: 60,
      },
      {
        type: "number",
        key: "equityCurrentValue",
        label: "Current Equity Value",
        prefix: "₹",
        min: 0,
        max: 100000000,
        step: 10000,
        default: 400000,
      },
      {
        type: "number",
        key: "debtCurrentValue",
        label: "Current Debt Value",
        prefix: "₹",
        min: 0,
        max: 100000000,
        step: 10000,
        default: 300000,
      },
      {
        type: "number",
        key: "goldCurrentValue",
        label: "Current Gold Value",
        prefix: "₹",
        min: 0,
        max: 100000000,
        step: 10000,
        default: 100000,
      },
    ],
    calculate(v) {
      const eqTarget = (v.equityTarget as number) / 100;
      const eqCurrent = v.equityCurrentValue as number;
      const debtCurrent = v.debtCurrentValue as number;
      const goldCurrent = v.goldCurrentValue as number;

      const totalPortfolio = eqCurrent + debtCurrent + goldCurrent;

      if (totalPortfolio <= 0) {
        return {
          totalPortfolio: 0,
          equityTarget: 0,
          equityCurrentPercent: 0,
          equityRebalance: 0,
          debtRebalance: 0,
          goldRebalance: 0,
          equityTargetValue: 0,
        };
      }

      const eqCurrentPercent = eqCurrent / totalPortfolio;
      const debtTarget = (1 - eqTarget) * 0.5;
      const goldTarget = (1 - eqTarget) * 0.5;

      const eqTargetValue = totalPortfolio * eqTarget;
      const debtTargetValue = totalPortfolio * debtTarget;
      const goldTargetValue = totalPortfolio * goldTarget;

      const eqRebalance = eqTargetValue - eqCurrent;
      const debtRebalance = debtTargetValue - debtCurrent;
      const goldRebalance = goldTargetValue - goldCurrent;

      return {
        totalPortfolio: Math.round(totalPortfolio),
        equityTarget: Math.round(eqTarget * 100),
        equityCurrentPercent: Math.round(eqCurrentPercent * 100 * 100) / 100,
        equityRebalance: Math.round(eqRebalance),
        debtRebalance: Math.round(debtRebalance),
        goldRebalance: Math.round(goldRebalance),
        equityTargetValue: Math.round(eqTargetValue),
      };
    },
    resultFields: [
      {
        key: "totalPortfolio",
        label: "Total Portfolio Value",
        format: "currency",
      },
      {
        key: "equityRebalance",
        label: "Equity Adjustment (buy/sell)",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "debtRebalance",
        label: "Debt Adjustment",
        format: "currency",
      },
      {
        key: "goldRebalance",
        label: "Gold Adjustment",
        format: "currency",
      },
    ],
    content: {
      sections: [
      {
            "title": "What Triggers Portfolio Rebalancing",
            "body": "Allocation drift: any asset class deviates >5-10% from target. Time-based: annual rebalancing regardless of drift (disciplined approach). Performance-based: rebalance when best performer becomes >60% of portfolio (concentration risk). Life-stage changes: shift allocation (more conservative as age increases). Market shocks: sudden corrections might need immediate tactical rebalancing. This calculator identifies drift and suggests rebalancing needs."
      },
      {
            "title": "Rebalancing Math & Implementation",
            "body": "Target: 60% equity (₹60L), 30% debt (₹30L), 10% cash (₹10L) on ₹100L portfolio. Current (after market movement): 70% equity (₹75L), 25% debt (₹25L), 5% cash (₹5L). Rebalancing: sell ₹15L equity, buy ₹5L debt + ₹10L cash. New state: 60% equity (₹60L), 30% debt (₹30L), 10% cash (₹10L)—restored! Calculator shows exact transactions needed without manual math."
      },
      {
            "title": "SEBI Guidelines & Regulatory Framework",
            "body": "SEBI recommends rebalancing for retail investors (reduces risk, improves discipline). Mutual funds' balanced funds internally rebalance automatically. Direct investors must rebalance manually. No tax penalty for rebalancing (within same demat account, no security transfer). Tax planning: harvest losses while rebalancing (sell underperformers showing losses to offset gains from winners)."
      },
      {
            "title": "Common Rebalancing Mistakes",
            "body": "Over-rebalancing (monthly): high trading costs, tax inefficiency. Under-rebalancing (never): allocation drifts dangerously (high risk concentration). Emotional rebalancing: panic-selling equity after crashes (selling low). Ignoring rebalancing: portfolio becoming too risky as winners compound. Optimal: rebalance annually or when drift >5-10%, use tax-loss harvesting during rebalancing, stay disciplined regardless of emotions."
      },
      {
            "title": "Rebalancing Strategy for Life Stages",
            "body": "Age 25-35 (accumulation): 80% equity, 15% debt, 5% cash (growth focus). Rebalance annually. Age 35-50 (peak earning): 70% equity, 25% debt, 5% cash (balance growth + stability). Rebalance quarterly. Age 50-60 (pre-retirement): 50% equity, 40% debt, 10% cash (de-risking). Rebalance quarterly. Age 60+ (retirement): 30% equity, 50% debt, 20% cash (income focus). Rebalance monthly if withdrawing. This calculator helps execute these life-stage rebalancing shifts systematically."
      }
],
      
      whatIs:
        "Portfolio rebalancing is the disciplined practice of readjusting your investment mix back to target allocation. Start with goal: 60% equity, 30% debt, 10% cash. After market movements, equity might become 70% (outperformance), debt 25%, cash 5%. Rebalancing means selling some equity, buying debt/cash to restore 60-30-10. In India's volatile market, rebalancing prevents \"return chasing\"—the mistake of holding winners too long (over-concentrated risk) or panic selling losers (buying high, selling low). Disciplined rebalancing (quarterly or annually) forces contrarian buying (buying debt when returns low, selling equity when returns soaring), improving long-term returns by 1-3% p.a. This calculator identifies when rebalancing is needed (e.g., equity drifted from 60% to 75%, rebalance trigger at 5-10% drift), calculates exact buy/sell amounts, and motivates discipline. SEBI-certified financial advisors emphasize rebalancing as crucial to long-term wealth preservation and risk management.",
      faqs: [
        {
          question:
            "How often should I rebalance?",
          answer:
            "Annually is standard. Rebalance when allocation drifts >5% from target. More frequent (quarterly) increases transaction costs. Less frequent (every 2-3 years) reduces discipline.",
        },
        {
          question:
            "Is rebalancing a taxable event?",
          answer:
            "Yes. Selling equity/debt at a gain = capital gains tax. Plan rebalancing in tax-loss-harvesting years. Use new investment capital for rebalancing when possible.",
        },
        {
          question:
            "Should I rebalance even if equity is performing well?",
          answer:
            "Yes! Rebalancing forces 'buy low, sell high' discipline. If equity surges (good returns), rebalance by selling some and locking profits. If debt surges, rebalance back to equity.",
        },
        {
          question:
            "What if I can't rebalance due to tax implications?",
          answer:
            "Direct new investments to underallocated assets. E.g., if equity is 75% (target 60%), invest new capital in debt until allocation rebalances naturally.",
        },
        {
          question:
            "What's a good asset allocation for Indians?",
          answer:
            "Age 20-40: 70% equity, 20% debt, 10% gold. Age 40-55: 60% equity, 30% debt, 10% gold. Age 55+: 50% equity, 40% debt, 10% gold. Adjust based on risk tolerance.",
        },
      ],
      relatedSlugs: [
        "investment/sip-calculator",
        "investment/mutual-fund-returns",
        "personal-finance/asset-allocation",
      ],
    },
  },
];
